import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DepartmentService } from '../../services/jrni/department.service';
import { ServicesService } from '../../services/jrni/services.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { BasketService } from '../../services/jrni/basket.service';
import { MemberService } from '../../services/jrni/member.service';
import {
	UntypedFormGroup,
	UntypedFormBuilder,
	Validators,
	UntypedFormControl,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { DatastoreService } from '../../services/datastore.service';
import {
	availableSlotsForDisplay,
	citizenshipCapacityOutcome,
} from '../../services/citizenship-capacity';
import { AppConfig } from '../../app.config';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '../../_alert';
import { _, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../services/jrni/api.service';
import moment from 'moment-timezone';
import { QuestionsService } from '../../services/questions.service';
import { HeyCentricService } from '../../services/hey-centric.service';
import * as generator from 'generate-password-browser';
import { BehaviorSubject, map, first, firstValueFrom } from 'rxjs';
import { Meta, MetaDefinition } from '@angular/platform-browser';

// RWRSPI-1091: a question answer holding an uploaded file. Was `data:image/`,
// which silently dropped the PDFs the WCC upload box now accepts. The mime
// segment is optional — FileReader emits `data:;base64,…` for a file the OS has
// no mapping for, and base64ToBlob already falls back to octet-stream.
const UPLOADED_FILE_ANSWER = /^data:[^;,]*;base64,/;

// Birth and death registration appointments need the perjury declaration on the
// summary page. Matched on global_id because selectedService is the child-company
// copy of the service, so its id is different at each location.
const LEGAL_DECLARATION_SERVICE_IDS = [48399, 48325, 48343, 48341];

@Component({
	selector: 'app-summary',
	templateUrl: './summary.component.html',
	styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnInit {
	selectedDepartment;
	selectedLocation;
	selectedService;
	pageTitle: string;
	summaryPageText: string;
	selectedSlot;
	selectedResource;
	selectedResourceObj;
	endSlot;
	termsAndConditions;
	submitted = false;
	items;
	member;
	client;
	address;
	resourceAddress;
	lat;
	long;
	config: any;
	bookingQuestions = [];
	filterSummaryData = [];
	bookingAnswers = [];
	displayTnCs: boolean;
	bookingSummaryData = [];
	companySummaryData = [];

	/**
	 * RWRSPI-1091: an uploaded file is carried as base64 in the question answer,
	 * so rendering it raw drops megabytes of `data:...` into the summary table.
	 * Show the customer the filename they picked instead — the answer itself is
	 * untouched, since the post-checkout attach reads it back off the booking.
	 */
	private displayAnswer(question: any): any {
		const answer = question?.answer;
		if (typeof answer !== 'string' || !UPLOADED_FILE_ANSWER.test(answer)) {
			return { answer, uploadedFile: null };
		}
		const filename =
			this.datastoreService.uploadFilenames[question.question_id] ||
			this.datastoreService.uploadFilenames[question.id] ||
			'File uploaded';
		return { answer: filename, uploadedFile: answer };
	}

	/**
	 * The displayed name falls back to "File uploaded" when the real filename is
	 * not in the store, which would save an extension-less file the OS cannot
	 * open. Give it one from the mime type in that case.
	 */
	private downloadFilename(displayed: string, mime: string): string {
		const name = displayed || 'upload';
		if (/\.[a-z0-9]{2,5}$/i.test(name)) return name;

		const extension = (mime.split('/')[1] || '').split('+')[0];
		return extension ? `${name}.${extension}` : name;
	}

	/**
	 * RWRSPI-1091: hand the customer back the file they attached. The data URI is
	 * turned into a blob first — Chrome refuses a top-level navigation to `data:`,
	 * and a multi-megabyte href is unwieldy regardless.
	 */
	downloadUploadedFile(detail: any): void {
		if (!detail?.uploadedFile) return;

		const [header, payload] = String(detail.uploadedFile).split(',');
		const mime = (header.match(/:(.*?);/) || [])[1] || 'application/octet-stream';
		const bytes = atob(payload);
		const buffer = new Uint8Array(bytes.length);
		for (let i = 0; i < bytes.length; i++) {
			buffer[i] = bytes.charCodeAt(i);
		}

		const url = URL.createObjectURL(new Blob([buffer], { type: mime }));
		const link = document.createElement('a');
		link.href = url;
		link.download = this.downloadFilename(detail.answer, mime);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		// Revoking straight away can cancel the download before the browser has
		// started reading the blob, so let the click settle first.
		setTimeout(() => URL.revokeObjectURL(url), 10000);
	}
	costBreakDown = [];
	total = 0;
	extraDetailsPresent = false;
	slotAvailable = true;
	dateOfBirth = '';
	checkoutStarted = false;
	// Set when the group citizenship ceremony can no longer seat this party. Drives the
	// notification banner on this page — see citizenshipCapacityStillHolds().
	citizenshipCapacityFull = false;
	citizenshipCapacitySpaces = 0;
	citizenshipCapacityRequired = 0;

	checkoutFailed = false;
	// Pre-payment gate (409) recovery state: one automated rebuild-and-retry is
	// allowed per page visit; if that also fails the journey is unrecoverable
	// and the customer is told explicitly that they have NOT been charged.
	gateRecoveryAttempted = false;
	paymentBlockedUnrecoverable = false;
	adminJourney = false;
	adminForm: UntypedFormGroup;
	adminFormSubmitted = false;
	confirmContinue = false;
	agreeDataPolicy = false;
	// Perjury declaration tick box - only shown for birth and death appointments.
	confirmLegalDeclaration = false;
	headerStyle: any;
	dataPolicy: string;
	dataPolicyCy: string;
	tncPolicy: string;
	store: BehaviorSubject<any>;
	addedItem: BehaviorSubject<any>;
	ceremonyFee: any;
	bookingPrice: any;
	citizenshipJourney = false;
	revealTextHeadings = [];
	agentMode: any;

	steps = [
		{ title: 'Booking questions', isActive: false },
		{ title: 'Choose appointment date and time', isActive: false },
		{ title: 'Your details', isActive: false },
		{ title: 'Summary', isActive: true },
		{ title: 'Confirmation', isActive: false },
	];

	meta = inject(Meta);
	constructor(
		private router: Router,
		private departmentService: DepartmentService,
		private servicesService: ServicesService,
		private availabilityService: AvailabilityService,
		private basketService: BasketService,
		private memberService: MemberService,
		private formBuilder: UntypedFormBuilder,
		private spinner: NgxSpinnerService,
		public datastoreService: DatastoreService,
		private alertService: AlertService,
		public translateService: TranslateService,
		private apiService: ApiService,
		private questionsService: QuestionsService,
		private heyCentricService: HeyCentricService
	) { }

	routerGoBack() {
		this.router.navigate(['/booking-details']);
	}

	restartJourney() {
		if (sessionStorage.getItem('journey')) {
			this.router.navigate(['start/' + sessionStorage.getItem('journey')]);
		} else {
			this.router.navigate(['/departments']);
		}
	}

	get form() {
		return this.adminForm.controls;
	}

	// Names of each validated citizen from the citizenship lookups, de-duplicated.
	get attendeeNames(): string[] {
		const names = (this.datastoreService.lookupResponses || [])
			.filter((response) => response && response.name)
			.map((response) => response.name);
		return Array.from(new Set(names));
	}

	/**
	 * Whether this booking is a birth or death registration appointment, which
	 * shows the Legal Information section. Falls back to the datastore's copy of
	 * the service in case the component's copy has not loaded yet.
	 */
	get showLegalDeclaration(): boolean {
		const globalId = Number(
			this.selectedService?.global_id ??
			this.datastoreService?.selectedService?.global_id
		);
		return LEGAL_DECLARATION_SERVICE_IDS.includes(globalId);
	}

	// True when the declaration is not needed, or it is needed and has been ticked.
	get legalDeclarationMet(): boolean {
		return !this.showLegalDeclaration || this.confirmLegalDeclaration;
	}

	linkClicked(ev) {
		if (ev.target.href) {
			console.log(ev.target.href);
			window.open(ev.target.href, '_blank').focus();
			ev.preventDefault();
		}
	}

	async ngOnInit() {
		const noGoogleReadAloud: MetaDefinition = {
			name: 'google',
			content: 'nopagereadaloud'
		};
		this.meta.addTag(noGoogleReadAloud, true);
		this.spinner.show();
		this.store = new BehaviorSubject(false);
		this.addedItem = new BehaviorSubject(false);
		this.scrollToTop();
		this.alertService.clear();

		this.agentMode = localStorage.getItem('agent');

		let configCompanyId = AppConfig.settings.core.company_id;
		if (this.datastoreService.isCitizenshipRoute) {
			this.citizenshipJourney = true;
			configCompanyId = AppConfig.settings.core.citizenship_company_id;
		}

		const _config: any = await this.apiService.getConfig(configCompanyId);
		this.config = _config;
		this.headerStyle = this.config.journey_config.appointments
			.notificationBanner
			? this.config.journey_config.appointments.notificationBanner
			: '';

		this.revealTextHeadings = this.config.journey_config.appointments.revealTextHeadingQuestionsOnSummary
		this.filterSummaryData = this.config.journey_config.appointments.hideQuestionsFromSummary;
		if (this.datastoreService.adminJourney) {
			this.adminJourney = true;
		}

		// restore the costbreakdown from datastore
		if (this.datastoreService.serviceFees) {
			this.costBreakDown = this.datastoreService.serviceFees;
		} else {
			this.costBreakDown = []; // clear cost breakdown; in future if we add to this more we dont want to clear but replace
		}
		console.log('costBreakDown', this.costBreakDown);

		this.servicesService.get().then(async (company) => {
			if (company === undefined) {
				// company not found, restart
				this.restartJourney();
			} else {
				// Set the questions and answer
				this.bookingQuestions = this.datastoreService.bookingQuestions;
				this.client = this.datastoreService.customerDetailsForm.getRawValue();

				// Get the selected location
				// Use this.departmentService.getLocation() if the services are set on the children departments
				// Use this.departmentService.get() if the services are set on the parent department
				this.departmentService.getLocation().then(async (location) => {
					this.selectedLocation = location;

					this.address = location.address;
					this.datastoreService.address = location.address;

					const resourceId = this.basketService.currentServiceItem?.resource_id;
					if (this.citizenshipJourney && resourceId) {
						const a: any = await this.apiService.getResourceAddress(location.id, resourceId);
						this.resourceAddress = a
							? [a.address1, a.address2, a.address3, a.address4, a.address5, a.postcode].filter(Boolean).join(', ')
							: '';
					}
				});
				this.availabilityService.getSelectedResource().then((resource) => {
					if (resource) {
						this.selectedResource = resource;
					}
				});
				this.availabilityService.getSelectedResourceObj().then((resource) => {
					if (resource) {
						this.selectedResourceObj = resource;
					}
				});
				// Get the selected slot
				if (this.datastoreService.selectedSlot) {
					this.availabilityService.getSelectedSlot().then((slot) => {
						this.selectedSlot = slot;
						this.availabilityService.getEndSlot().then((slot) => {
							this.endSlot = slot;
						});
					});
				}
				// don't know exactly what the id of the date question is
				if (
					this.datastoreService.customerDetailsForm.controls['1'] !== undefined
				)
					this.dateOfBirth = this.getDOB(
						this.datastoreService.customerDetailsForm.controls['1'].value
					);
				if (
					this.datastoreService.customerDetailsForm.controls['279'] !==
					undefined
				)
					this.dateOfBirth = this.getDOB(
						this.datastoreService.customerDetailsForm.controls['279'].value
					);
				if (
					this.datastoreService.customerDetailsForm.controls['34'] !== undefined
				)
					this.dateOfBirth = this.getDOB(
						this.datastoreService.customerDetailsForm.controls['34'].value
					);

				// Use this.departmentService.getLocation() if the services are set on the children departments
				// Use this.departmentService.get() if the services are set on the parent department
				this.departmentService.get().then((selectedDepartment) => {
					console.log('set dept', selectedDepartment);
					if (selectedDepartment === undefined) {
						this.restartJourney();
					} else {
						this.selectedDepartment = selectedDepartment;

						// Get the configurable data
						if (this.selectedDepartment.hasOwnProperty('extra')) {
							if (this.adminJourney) {
								this.dataPolicy = `Advise the customer that they can see our Privacy Policy for details on how Westminster Council processes their information. They can find the policy <a href="" target="_blank">here</a>.`;
							} else {
								this.selectedDepartment.extra.hasOwnProperty(
									'privacy_statement_text'
								)
									? (this.dataPolicy =
										this.selectedDepartment.extra.privacy_statement_text)
									: (this.dataPolicy = 'data policy not set');

								this.selectedDepartment.extra.hasOwnProperty(
									'privacy_statement_text_welsh'
								)
									? (this.dataPolicyCy =
										this.selectedDepartment.extra.privacy_statement_text_welsh)
									: (this.dataPolicyCy = 'Welsh data policy not set');
							}

							// Set the title on the login page to the title from the business question
							this.selectedDepartment.extra.hasOwnProperty('review_page_title')
								? (this.pageTitle =
									this.selectedDepartment.extra.review_page_title)
								: (this.pageTitle = 'Review title not set');
							// Set the extra text on the page to the text taken from the business question
							this.selectedDepartment.extra.hasOwnProperty('summary_page_text')
								? (this.summaryPageText =
									this.selectedDepartment.extra.summary_page_text)
								: null;
							// Tncs
							this.getTermsAndConditions();
						}

						// Use this.departmentService.getLocation() if the services are set on the children departments
						// Use this.departmentService.get() if the services are set on the parent department
						this.departmentService.getLocation().then((location) => {
							this.selectedLocation = location;
						});

						// Get the selected service
						this.servicesService.get().then((service) => {
							this.selectedService = service;
							this.selectedService.calculated_duration =
								Number(this.datastoreService.extraDuration) +
								Number(this.selectedService.durations[0]);


							this.datastoreService.bookingQuestions.forEach((question) => {
								if (this.datastoreService.bookingForm.value[question['id']]) {
									this.bookingSummaryData.push({
										name: question.name,
										answer:
											this.datastoreService.bookingForm.value[question.id],
										detail_type: question.detail_type,
										price: question.price,
										reference: question.conditionals.question_reference
											? question.conditionals.question_reference
											: '',
										question_id: question.id
									});
								} else if (
									this.datastoreService.companyQuestionsForm.value[
									question['id']
									]
								) {
									this.bookingSummaryData.push({
										name: question.name,
										answer:
											this.datastoreService.companyQuestionsForm.value[
											question.id
											],
										detail_type: question.detail_type,
										price: question.price,
										reference: question.conditionals.question_reference
											? question.conditionals.question_reference
											: '',
										question_id: question.id
									});
								} else if (
									this.datastoreService.startQuestions.value[question['id']]
								) {
									this.bookingSummaryData.push({
										name: question.name,
										answer:
											this.datastoreService.startQuestions.value[question.id],
										detail_type: question.detail_type,
										price: question.price,
										reference: question.conditionals.question_reference
											? question.conditionals.question_reference
											: '',
										question_id: question.id
									});
								} else if (
									this.revealTextHeadings.includes(question['id'])
								) {
									this.bookingSummaryData.push({
										name: question.name,
										answer:
											this.datastoreService.startQuestions.value[question.id],
										detail_type: question.detail_type,
										price: question.price,
										reference: question.conditionals.question_reference
											? question.conditionals.question_reference
											: '',
										question_id: question.id
									});
								}

								// filter out the questions that are in the this.filterSummaryData array
								this.bookingSummaryData = this.bookingSummaryData.filter(item => !this.filterSummaryData.includes(item.question_id));

							});

							this.bookingSummaryData = this.bookingSummaryData.map(
								(question) => {
									const updatedName = question.name.replace(
										/\{(.*?)\}/g,
										(match, refKey) => {
											const trimmedRef = refKey.trim();
											const found = this.bookingSummaryData.find(
												(q) => q.reference == trimmedRef
											);
											return found ? found.answer : match;
										}
									);
									return {
										...question,
										name: updatedName,
										...this.displayAnswer(question),
									};
								}
							);

							this.datastoreService.companyQuestions.forEach((question) => {
								if (
									this.datastoreService.companyQuestionsForm.value[
									question['id']
									]
								) {
									this.companySummaryData.push({
										name: question.name,
										answer:
											this.datastoreService.companyQuestionsForm.value[
											question.id
											],
										detail_type: question.detail_type,
										price: question.price,
									});
								}
							});


							// Check if the questions contain any other purchase details
							const extraDetails = this.bookingSummaryData.filter(
								(elem) => elem.detail_type !== 'check-price'
							);
							extraDetails.length > 0
								? (this.extraDetailsPresent = true)
								: (this.extraDetailsPresent = false);
							// Calculate purchase total
							const paidItems = this.bookingSummaryData.filter(
								(item) => item.detail_type === 'check-price'
							);

							this.total = paidItems
								.map((items) => items.price)
								.reduce((a, b) => a + b, 0) + this.selectedService?.price;

							console.log("booking summary data");
							console.log(this.bookingSummaryData);
						});

						// Get the selected slot
						this.availabilityService.getSelectedSlot().then((slot) => {
							this.selectedSlot = slot;
							console.log(slot);
						});
					}
				});
			}
		});
		if (this.adminJourney) {
			this.adminForm = new UntypedFormGroup({});

			const adminAmount = new UntypedFormControl('', [Validators.required]);
			const adminRef = new UntypedFormControl('', [Validators.required]);
			const adminCertRemoval = new UntypedFormControl('');
			const adminNoteStatus = new UntypedFormControl('', [Validators.required]);

			this.adminForm.addControl('amount', adminAmount);
			this.adminForm.addControl('reference', adminRef);
			this.adminForm.addControl('certificate_removal', adminCertRemoval);
			this.adminForm.addControl('note', adminNoteStatus);
		}
	}

	getDOB(dateString) {
		const birthDate = new Date(dateString);
		return moment(birthDate).format('DD/MM/YYYY');
	}

	getTermsAndConditions() {
		if (this.selectedDepartment.hasOwnProperty('extra')) {
			this.displayTnCs = true;
			// Check if the terms and conditions are on/off
			if (
				this.selectedDepartment.extra.hasOwnProperty('display_tncs') &&
				this.selectedDepartment.extra.display_tncs.toLowerCase() === 'yes'
			) {
				this.termsAndConditions = '';
				// Loop through all the extra pre defined fields
				const extraQuestions = this.selectedDepartment.extra;
				for (const key in extraQuestions) {
					if (extraQuestions.hasOwnProperty(key)) {
						const element = extraQuestions[key];
						if (key.indexOf('terms_and_conditions') >= 0) {
							if (!this.termsAndConditions) {
								this.termsAndConditions += element;
							} else {
								this.termsAndConditions += ' ' + element;
							}
						}
					}
				}
			} else {
				this.displayTnCs = false;
			}
		}
	}

	// create client
	async createClient(adminJourney: boolean): Promise<boolean> {
		const customerQs = [];
		let clientCreated = false;

		// For each question in the form check if the customer question exists and if so add to customerAnswers []
		for (const control in this.datastoreService.customerDetailsForm.value) {
			if (
				this.datastoreService.customerDetailsForm.value.hasOwnProperty(control)
			) {
				const element =
					this.datastoreService.customerDetailsForm.value[control];
				const question = this.datastoreService.customerQuestions.filter(
					(question) => question.id.toString() === control
				);
				let q = {};
				if (question.length > 0 && element !== '') {
					q = {
						id: question[0].id,
						answer: element,
					};

					customerQs.push(q);
				}
			}
		}

		const customerDetailsValue = this.datastoreService.customerDetailsForm;

		const params = {
			first_name: customerDetailsValue.controls.first_name.value,
			last_name: customerDetailsValue.controls.last_name.value,
			email: customerDetailsValue.controls.email_address.value,
			mobile: customerDetailsValue.controls.contact_number.value,
			// mobile: customerDetailsValue.controls.mobile_number.value,
			// postcode: customerDetailsValue.postcode,
			// .trim()
			// .replace(" ", ""),
			member_type: '2',
			send_welcome_email: false,
		};

		let clientRes: any;
		const found: any = await this.apiService.getBookingsForUser(
			this.selectedLocation,
			this.selectedDepartment,
			params.email
		);

		if (adminJourney) {
			// we DO NOT need to create a client in this instance.
			try {
				if (found !== 'User not found') {
					this.datastoreService.client = found;
					console.log(
						'datastore client (pre-existing member ADMIN)',
						this.datastoreService.client
					);
					this.datastoreService.clientCreated = true;
				} else {
					clientRes = await this.selectedDepartment.$post('client', {}, params);
					if (clientRes) {
						this.datastoreService.client = clientRes;
						console.log(
							'datastore client (newly created ADMIN)',
							this.datastoreService.client
						);
						this.datastoreService.clientCreated = true;
						clientCreated = true;
					}
				}
				this.store.next(true);
				return clientCreated;
			} catch (err) {
				console.error(err);
			}
		} else {
			try {
				if (found !== 'User not found') {
					// now we sso if the are a member
					if (found.member_type && found.member_type == 2) {
						try {
							const currentAuthToken = localStorage.getItem('auth_token');
							if (currentAuthToken) {
								localStorage.setItem('_original_auth_token', currentAuthToken);
							}
							clientRes = await this.selectedDepartment.$post(
								'client',
								{},
								params
							);
							if (clientRes) {
								this.datastoreService.client = clientRes;
								this.member = clientRes;
								console.log('datastore client', this.datastoreService.client);
								this.datastoreService.clientCreated = true;
								const newAuthToken = localStorage.getItem('auth_token');
								localStorage.setItem('auth_token', newAuthToken);
								clientCreated = true;
							}
						} catch (err) {
							console.error(err);
						}
					} else {
						// Now we need to SSO them in!
						// Dead-session cleanup: the SSO login below rotates the JRNI session
						// token, so this journey's basket (built under the CURRENT token) can
						// never be checked out — free its slot before rotating. Targeted to
						// THIS journey's basket only: tabs share localStorage, so a
						// session-wide delete here can destroy a basket another tab has a
						// payment URL minted against (payment taken, booking not created).
						await this.apiService.deleteBaskets(this.selectedLocation, this.basketService.currentBasket?.id);
						const currentAuthToken = localStorage.getItem('auth_token');
						if (currentAuthToken) {
							localStorage.setItem('_original_auth_token', currentAuthToken);
						}
						const token: any = await this.apiService.generateSSOToken(
							this.selectedDepartment.id,
							params.first_name,
							params.last_name,
							params.email
						);
						console.log(token);
						clientRes = (await this.apiService.ssoLogin(
							this.selectedDepartment.id,
							token.token
						)) as any;
						this.datastoreService.client = clientRes._embedded.members[0];
						this.member = this.datastoreService.client;
						localStorage.setItem('auth_token', clientRes.auth_token);
						clientCreated = true;
					}
				} else {
					clientRes = await this.selectedDepartment.$post('client', {}, params);
					if (clientRes) {
						this.datastoreService.client = clientRes;
						this.member = clientRes;
						console.log(
							'datastore client (newly created NON-ADMIN)',
							this.datastoreService.client
						);
						this.datastoreService.clientCreated = true;
						clientCreated = true;
					}
				}
				this.store.next(true);
			} catch (err) {
				console.error(err);
			}
		}
		return clientCreated;
	}

	// forceNewBasket: skip the session-basket lookup and build a fresh basket +
	// item. Used when the JRNI session token was rotated (client create / SSO) —
	// the old session's basket is unreachable under the new token, and the SDK's
	// basket lookup may still answer with it via the OLD internal token.
	async addItem(forceNewBasket = false) {
		// Get the selected slot from the previous page
		const selectedSlot = await this.availabilityService.getSelectedSlot();
		console.log('selectedSlot', selectedSlot);

		// from the config, get the question ID for the data policy
		const dataPolicyQuestionId = this.config.journey_config.appointments.privacyConsent.bookingProfile;

		console.log(`${dataPolicyQuestionId} - ${this.agreeDataPolicy}`);

		this.bookingAnswers.push({
			id: `${dataPolicyQuestionId}`,
			answer: this.agreeDataPolicy ? true : null,
		});

		// Get the booking answers and add them to an array ready to add to basket
		for (const key in this.datastoreService.bookingForm.value) {
			{
				this.bookingAnswers.push({
					id: key,
					answer: this.datastoreService.bookingForm.value[key],
				});
			}
		}

		for (const key in this.datastoreService.companyQuestionsForm.value) {
			{
				const isAuxPostcode = key.startsWith('postcode_') || (key.endsWith('_postcode') && key.slice(0, -9) in this.datastoreService.companyQuestionsForm.value);
				if (!isAuxPostcode) {
					this.bookingAnswers.push({
						id: key,
						answer: this.datastoreService.companyQuestionsForm.value[key],
					});
				}
			}
		}

		for (const key in this.datastoreService.companyAnswers.value) {
			{
				if (!key.includes('postcode')) {
					// ditch any postcode lookups
					this.bookingAnswers.push({
						id: key,
						answer: this.datastoreService.companyAnswers.value[key],
					});
				}
			}
		}

		// const questions = (await this.questionsService.getCompanyQuestions().toPromise())['_embedded']
		// const altQuestions = (await this.questionsService.getAltCompanyQuestions().toPromise())['_embedded']

		// const altQuestions:any = await this.questionsService.getAltCompanyQuestions().toPromise().then(questions => {return questions})
		// add the extra questions from the departments page
		// we do all this because we get the answers to several questions before we know the service
		//so we need to convert the base service's questions too the alt ones, and the only shared identifier (that we can access) is name
		if (this.datastoreService.companyQuestions) {
			for (const key in this.datastoreService.companyQuestionsForm.value) {
				const myQuestion = this.datastoreService.companyQuestions.filter(
					(x) => x.id.toString() === key
				)[0];
				// const myALtQuestion = altQuestions.questions.filter(x => x.name === myQuestion.name)[0]
				if (myQuestion) {
					if (myQuestion.length > 0) {
						this.bookingAnswers.push({
							id: key,
							answer: this.datastoreService.companyQuestionsForm.value[key],
						});
					}
				}
				// if(myALtQuestion !== undefined){
				//   this.bookingAnswers.push({
				//     id: myALtQuestion.id,
				//     answer: this.datastoreService.companyQuestionsForm.value[key],
				//   });
				// } else{
				//   alert("administrative error: Denbigh and Ruthin's question titles must match those of the other companies (this includes both translations)")
				//   return
				// }
			}
		}
		// Is the booking in Welsh, Auto populate this question
		// const usingWelsh = this.translateService.currentLang == "cy" ? 'Welsh' : 'English';
		// this.bookingAnswers.push({ id: 271, answer: usingWelsh })
		// this.bookingAnswers.push({ id: 39, answer: usingWelsh })

		this.datastoreService.bookingAnswers = this.bookingAnswers;

		console.log(this.bookingAnswers);

		// Add item to the basket
		// const res = await this.basketService.updateItemV1(
		//   this.selectedLocation,
		//   this.selectedService.id,
		//   this.selectedService.calculated_duration,
		//   selectedSlot.start,
		//   this.bookingAnswers,
		//   this.datastoreService.client.id,
		//   this.selectedResource
		// )

		if (this.datastoreService.adminJourney) {
			try {
				const updateServiceItem = await this.basketService.updateItemV1(
					this.selectedLocation,
					this.selectedService.id,
					this.selectedService.calculated_duration,
					selectedSlot.start,
					this.bookingAnswers,
					this.datastoreService.client.id,
					this.selectedResource,
					this.datastoreService.staff_set
				);
				console.log(updateServiceItem);
				// api.service swallows HTTP failures and returns the error message as a STRING
				// instead of throwing, so a failed update reads as success here. Anything that is
				// not a service item means the item still carries the resource JRNI auto-assigned
				// — i.e. the wrong room — so fail loudly rather than paying for it.
				if (!updateServiceItem || typeof updateServiceItem !== 'object') {
					throw new Error(
						`Service item update failed: ${JSON.stringify(updateServiceItem)}`
					);
				}
				this.addedItem.next(updateServiceItem);
			} catch (err) {
				console.error(err);
				this.alertService.error(
					this.translateService.instant('SUMMARY.INVALID_SLOT')
				);
				this.spinner.hide();
				this.scrollToTop();
				throw err;
			}
		} else {
			try {
				const basket: any = forceNewBasket
					? null
					: await this.basketService.getCurrentBasket(this.selectedLocation);
				console.log('FOUND BASKET', basket);
				if (!basket) {
					// if no baskets on new auth token, create one then add the service item.
					// Awaited (was a fire-and-forget subscribe): addItem() must not return
					// until the item exists, and a failure in here must reach the caller's
					// catch instead of vanishing inside an observable callback.
					const response: any = await firstValueFrom(
						this.apiService.createBasket(this.selectedLocation)
					);
					// The basket-creation response can carry a fresh session token — store it,
					// or the payment URL gets minted under a token that cannot see this basket.
					if (response?.headers?.has('auth-token')) {
						localStorage.setItem('auth_token', response.headers.get('auth-token'));
					}
					console.log('newBasket', response.body);
					const serviceItem: any = await this.apiService.addServiceItem(
						this.selectedLocation,
						response.body,
						this.selectedSlot.start,
						this.selectedService,
						this.bookingAnswers,
						this.selectedService.calculated_duration,
						this.selectedResource
					);
					console.log(
						'newBasket',
						response.body,
						'serviceItem',
						serviceItem
					);
					this.basketService.currentBasket = response.body;
					this.basketService.currentServiceItem = serviceItem;
					// api.service swallows HTTP failures and returns the error message as a STRING
					// instead of throwing, so a failed update reads as success here. Anything that is
					// not a service item means the item still carries the resource JRNI auto-assigned
					// — i.e. the wrong room — so fail loudly rather than paying for it.
					if (!serviceItem || typeof serviceItem !== 'object') {
						throw new Error(
							`Service item update failed: ${JSON.stringify(serviceItem)}`
						);
					}
					this.addedItem.next(serviceItem);
				} else {
					const updateServiceItem = await this.apiService.updateServiceItem(
						this.selectedLocation,
						this.basketService.currentBasket,
						this.basketService.currentServiceItem,
						{
							company_id: this.selectedLocation.id,
							service_id: this.selectedService.id,
							duration: this.selectedService.calculated_duration,
							person_id: this.datastoreService.staff_set,
							start: `${selectedSlot.start}`,
							resource_id: this.apiService.resolveResourceId(this.selectedResource),
							questions: this.bookingAnswers,
						}
					);
					console.log(updateServiceItem);
					// api.service swallows HTTP failures and returns the error message as a STRING
					// instead of throwing, so a failed update reads as success here. Anything that is
					// not a service item means the item still carries the resource JRNI auto-assigned
					// — i.e. the wrong room — so fail loudly rather than paying for it.
					if (!updateServiceItem || typeof updateServiceItem !== 'object') {
						throw new Error(
							`Service item update failed: ${JSON.stringify(updateServiceItem)}`
						);
					}
					this.addedItem.next(updateServiceItem);
				}
			} catch (err) {
				console.error(err);
				this.alertService.error(
					this.translateService.instant('SUMMARY.INVALID_SLOT')
				);
				this.spinner.hide();
				this.scrollToTop();
				throw err;
			}
		}
	}

	/**
	 * Re-check that the group citizenship ceremony can still seat this party.
	 *
	 * The calendar checked it when the slot was chosen, but filling in the details takes
	 * minutes and the places are shared with everyone else booking that ceremony. The next
	 * thing this page does is write the pre-basket row and hand the customer to the payment
	 * gateway, after which the booking is made server-side whether or not the ceremony can
	 * seat them — so this is the last point at which a shortfall costs nobody anything.
	 *
	 * A shortfall is not an error. It is a fact about the ceremony with an obvious way
	 * forward, so it is rendered as a notification banner on this page instead of going
	 * through alertService, whose summary is headed "There is a problem" — wording that
	 * alarms applicants and tells them nothing they can act on.
	 */
	private async citizenshipCapacityStillHolds(): Promise<boolean> {
		this.citizenshipCapacityFull = false;

		if (
			!this.datastoreService.isCitizenshipRoute ||
			!this.datastoreService.groupCitizenshipBooking
		) {
			return true;
		}

		// The calendar strips the UTC offset before asking, and claim rows are matched to
		// the minute, so the same string has to go out from here.
		const slotStart = `${this.selectedSlot?.start || ''}`.split('+')[0];
		if (!slotStart || !this.selectedLocation?.id || !this.selectedService?.id) {
			// Nothing to ask about. The calendar has already gated this slot once, so let the
			// journey carry on rather than refuse a payment over missing local state.
			console.warn('[citizenship-capacity] summary re-check skipped', {
				slotStart,
				location: this.selectedLocation?.id,
				service: this.selectedService?.id,
			});
			return true;
		}

		const requiredPlaces = this.citizenshipPlacesRequired();

		let availability: any;
		this.spinner.show();
		try {
			availability = await this.apiService.citizenshipAvailability(
				this.selectedLocation.id,
				this.selectedService.id,
				slotStart
			);
		} catch (err) {
			console.error('[citizenship-capacity] summary re-check failed', err);
			this.spinner.hide();
			return true;
		}
		this.spinner.hide();

		const outcome = citizenshipCapacityOutcome(availability, requiredPlaces);
		console.log('[citizenship-capacity] summary re-check', {
			requiredPlaces,
			availability,
			outcome,
		});

		// 'unknown' means citizenshipAvailability handed back its error message instead of a
		// slot count. A request that failed is not a ceremony that is full, so say nothing.
		if (outcome !== 'full') {
			return true;
		}

		this.citizenshipCapacitySpaces = availableSlotsForDisplay(availability);
		this.citizenshipCapacityRequired = requiredPlaces;
		this.citizenshipCapacityFull = true;
		// Drop any "There is a problem" summary left over from an earlier attempt, so the
		// banner is the only thing the customer has to read.
		this.alertService.clear();
		this.scrollToTop();
		// Scrolling alone tells a screen reader nothing, and Angular has not rendered the
		// banner yet — move the reading position onto it once it exists.
		setTimeout(() => document.getElementById('citizenship-capacity-banner')?.focus(), 0);
		return false;
	}

	/**
	 * Places this booking needs.
	 *
	 * The calendar compares two numbers against the same slot count — the applicants
	 * counted on the questions page (SUP-2588) and the group guest count (RWRSPI-985) —
	 * which amounts to requiring room for the larger of the two. The calendar also refuses
	 * a slot outright when the guest question is missing or unanswered; that is a problem
	 * with the form rather than with the ceremony, so it is deliberately not repeated
	 * here, where it would reach the customer as "this time is no longer available".
	 */
	private citizenshipPlacesRequired(): number {
		const applicants = Number(this.datastoreService.citizenshipRequiredPlaces) || 1;

		// Controls are keyed by question id, so the guest question is resolved by name.
		const guestQuestion = (this.datastoreService.companyQuestions || []).find(
			(q: any) =>
				q?.original_name === 'Number of guests (non applicants)' &&
				Object.values(q?.conditionals?.show || {}).includes('Group ceremony')
		);
		const guests = parseInt(
			this.datastoreService.companyQuestionsForm?.controls?.[guestQuestion?.id]?.value,
			10
		);

		return Number.isFinite(guests) && guests > applicants ? guests : applicants;
	}

	/** Back to the calendar, to choose a time that still has room. */
	chooseAnotherDateAndTime() {
		this.citizenshipCapacityFull = false;
		this.router.navigate(['/calendar']);
	}

	async startCheckout(telephony: boolean) {
		// Birth and death appointments cannot go through without the perjury
		// declaration. The buttons are already disabled until it is ticked - this
		// guard stops any other route into checkout from skipping it.
		if (!this.legalDeclarationMet) {
			return;
		}

		// Take the buttons out before the first await. The re-check below makes a request,
		// and until this is set a second click would start a second checkout.
		this.checkoutStarted = true;

		// The ceremony can fill up while the customer is filling in their details, and
		// everything past this point commits them — see citizenshipCapacityStillHolds().
		if (!(await this.citizenshipCapacityStillHolds())) {
			// Places can free up again, so leave the customer able to try this slot once
			// more as well as able to pick another one.
			this.checkoutStarted = false;
			return;
		}

		if (this.adminJourney) {
			this.submitted = true;
			if (!telephony) {
				// if (this.adminForm.valid) { // Admin form no longer in the code
				// do some admin stuff
				this.checkoutStarted = true;
				this.spinner.show();

				// Check if selected slot is still available
				let slotAvailable: boolean;

				await this.apiService
					.checkSlotAvailability(
						this.selectedDepartment,
						this.selectedSlot.start
					)
					.then((res: any) => {
						slotAvailable = res.available;
					});

				if (slotAvailable === false) {
					// If unavailable, then alert user
					this.alertService.error(
						this.translateService.instant('SUMMARY.INVALID_SLOT')
					);
					this.scrollToTop();
					this.slotAvailable = false;
					this.spinner.hide();
					this.checkoutStarted = false;
				} else {
					// If available then checkout
					// this.checkout()
					if (!telephony) {
						await this.adminCheckout();
					}
				}
				// }
			} else {
				this.checkoutStarted = true;
				this.spinner.show();

				// Check if selected slot is still available
				let slotAvailable: boolean;

				await this.apiService
					.checkSlotAvailability(
						this.selectedDepartment,
						this.selectedSlot.start
					)
					.then((res: any) => {
						slotAvailable = res.available;
					});

				if (slotAvailable === false) {
					// If unavailable, then alert user
					this.alertService.error(
						this.translateService.instant('SUMMARY.INVALID_SLOT')
					);
					this.scrollToTop();
					this.slotAvailable = false;
					this.spinner.hide();
					this.checkoutStarted = false;
				} else {
					// If available then checkout
					// this.checkout()
					await this.telephonePayment();
				}
			}
		} else {
			this.checkoutStarted = true;
			this.spinner.show();

			try {
				const priceData = await this.preCheckoutExtras();
				console.log('priceData', priceData);
				await this.preBasketPayment(priceData.paymentAmount, false);
			} catch (err) {
				console.error(err);
				this.spinner.hide();
				this.checkoutStarted = false;
			}
		}
	}

	async adminCheckout() {
		this.alertService.clear();
		this.adminFormSubmitted = true;
		let noNotifications = true;

		const customerDetailsValue = this.datastoreService.customerDetailsForm;

		const params = {
			first_name: customerDetailsValue.controls.first_name.value,
			last_name: customerDetailsValue.controls.last_name.value,
			email: customerDetailsValue.controls.email_address.value,
			mobile: customerDetailsValue.controls.contact_number.value,
			// mobile: customerDetailsValue.controls.mobile_number.value,
			// postcode: customerDetailsValue.postcode,
			// .trim()
			// .replace(" ", ""),
			member_type: '2',
			send_welcome_email: false,
		};
		let clientRes;
		const found: any = await this.apiService.getBookingsForUser(
			this.selectedLocation,
			this.selectedDepartment,
			params.email
		);
		if (found == 'User not found') {
			// make the client
			try {
				clientRes = await this.selectedDepartment.$post('client', {}, params);
				if (clientRes) {
					this.datastoreService.client = clientRes;
					console.log('datastore client', this.datastoreService.client);
					this.datastoreService.clientCreated = true;
				}
			} catch (err) {
				console.error(err);
			}
		} else {
			// get teh clients ID
			console.log(found);
			this.datastoreService.client = found;
			// this.memberService.setMember(found);
		}
		try {
			await this.addItem();
		} catch (err) {
			// addItem() has already surfaced the error to the customer. Do not fall through:
			// the service item can still hold whatever resource JRNI auto-assigned, which is
			// exactly how bookings land on the wrong resource.
			console.error('Aborting checkout — service item update failed', err);
			this.checkoutStarted = false;
			this.spinner.hide();
			return;
		}

		if (
			this.departmentService.selectedDepartment.hasOwnProperty('extra') &&
			this.departmentService.selectedDepartment.extra.hasOwnProperty(
				'login_required'
			)
		) {
			this.departmentService.selectedDepartment.extra.login_required.toLowerCase() ===
				'yes'
				? (this.member = await this.memberService.getMember())
				: (this.member = await this.datastoreService.client);
		} else {
			this.member = await this.datastoreService.client;
		}

		// if (!this.datastoreService.adminJourney && this.datastoreService.selectedService.extra?.online_payment == 'Yes') {
		noNotifications = true;
		// }

		if (this.datastoreService.adminJourney) {
			await this.basketService
				.checkoutBasketV1(this.selectedLocation, this.member, noNotifications)
				.then(async (res: any) => {
					// Get the purchase
					await this.afterCheckout(res);
				})
				.catch((error: HttpErrorResponse) => {
					console.log(error);
					if (error['response'].status === 409) {
						this.alertService.error(
							this.translateService.instant('SUMMARY.INVALID_SLOT')
						);
					} else {
						this.alertService.error(
							this.translateService.instant('COMMON.LOAD_ISSUE')
						);
					}
					this.spinner.hide();
					this.checkoutStarted = false;
					this.scrollToTop();
				});
		} else {
			try {
				const checkout = await this.apiService.basketCheckout(
					this.selectedLocation,
					this.basketService.currentBasket,
					this.datastoreService.client
				);
				await this.afterCheckout(checkout);
			} catch (error) {
				console.error(error);
				if (error['response'].status === 409) {
					this.alertService.error(
						this.translateService.instant('SUMMARY.INVALID_SLOT')
					);
				} else {
					this.alertService.error(
						this.translateService.instant('COMMON.LOAD_ISSUE')
					);
				}
				this.spinner.hide();
				this.checkoutStarted = false;
				this.scrollToTop();
			}
		}
	}

	async postBasketCheckout(company, basket, client) {
		if (isNaN(this.datastoreService.totalCost)) {
			this.datastoreService.totalCost = 0;
		}
		const checkout: any = await this.apiService.basketCheckout(
			company,
			basket,
			{ id: client.id }
		);

		console.log(checkout);
		const purchase: any = await this.apiService.findPurchase(
			this.selectedLocation,
			checkout.id
		);
		console.log(purchase);

		const booking = purchase._embedded.bookings[0];
		const member = purchase._embedded.client;
		this.member = member;
		this.datastoreService.booking = booking;
		this.datastoreService.purchase = purchase;
		console.log(booking);

		// await this.apiService.sendFrontSheetEmail(booking); // sendCommunicationEmail
		let ceremonyFee;
		try {
			if (this.datastoreService.serviceFees.length > 0) {
				// find ceremony base cost
				const noQuestionId = this.datastoreService.serviceFees.find(
					(item) => item.questionid == undefined
				);
				console.log('noQuestionId', noQuestionId);
				if (noQuestionId) {
					ceremonyFee = noQuestionId;
					// Leave the base cost in serviceFees - the fee table renders that
					// array, and the extra-item loop below skips rows with no questionid.
				}
			}
			console.log('ceremonyFee', ceremonyFee);

			// ceremonyFee = this.datastoreService.serviceFees.shift();
		} catch (err) {
			console.log(err);
		}

		let paymentAmount = 0;
		let bookingPrice = 0;
		let chargePrice = 0;
		let certs = 0;
		let foundExtraItemIds = [];
		let foundExtraItemCharges = [];

		let certsToRemoveCount = 0;

		try {
			// link booking to extra item ONLY IF we are not an admin journey, but we always want to calculate the price.
			if (this.datastoreService.config.hasOwnProperty('journey_config')) {
				if (
					this.datastoreService.config.journey_config.hasOwnProperty(
						'appointments'
					)
				) {
					// if (this.datastoreService.config.journey_config.ceremonies.hasOwnProperty('fees_extra_item_id')) {
					//   const extraItem = this.datastoreService.config.journey_config.ceremonies.fees_extra_item_id.trim();
					//   await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem, ceremonyFee);
					// }
					if (this.datastoreService.selectedService.extra?.fees_extra_item_id) {
						const extraItem =
							this.datastoreService.selectedService.extra?.fees_extra_item_id.trim();
						if (extraItem && extraItem.length > 0) {
							// paymentAmount += ceremonyFee.deposit;
							// paymentAmount = this.datastoreService.totalCost;
							bookingPrice += ceremonyFee.cost;
							// if (!this.adminJourney) {
							await this.apiService.linkExtraItem(
								this.selectedLocation.id,
								this.datastoreService.selectedService.id,
								booking.id,
								extraItem,
								ceremonyFee
							);
							// }
						}
					}
					// now link other items
					console.log('serviceFees', this.datastoreService.serviceFees);
					for (const extraItem of this.datastoreService.serviceFees) {
						if (extraItem.questionid) {
							const question = this.datastoreService.companyQuestions.find(
								(q) => q.id == extraItem.questionid
							);
							if (question) {
								if (question.help_text) {
									const schema: any = JSON.parse(question.help_text);
									if (schema['extra-item']) {
										const extraItemId = schema['extra-item'];
										foundExtraItemIds.push(extraItemId);
										foundExtraItemCharges.push(
											(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0)
										);
										// if (question.id == this.datastoreService.config.journey_config.ceremonies.included_certificates_question) {
										// paymentAmount += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
										// bookingPrice += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
										// chargePrice += Number(extraItem.cost) || 0;
										bookingPrice +=
											(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
										// bookingPrice += extraItem.cost;
										// }
										certs++;
										// Always link extra items
										// if (!this.adminJourney) {
										await this.apiService.linkExtraItem(
											this.selectedLocation.id,
											this.datastoreService.selectedService.id,
											booking.id,
											extraItemId,
											extraItem
										);
										// }
									} else {
										console.log('Missing extra item');
									}
								} else {
									console.log('Missing schema');
								}
							} else {
								console.log(`Cant find question ${extraItem.questionid}`);
								chargePrice += Number(extraItem.cost) || 0;
								bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
								await this.apiService.linkExtraItem(
									this.selectedLocation.id,
									this.datastoreService.selectedService.id,
									booking.id,
									extraItem.item_id,
									extraItem
								);
							}
						} else {
							console.log('No question ID on extra item');
							chargePrice += Number(extraItem.cost) || 0;
							bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
							await this.apiService.linkExtraItem(
								this.selectedLocation.id,
								this.datastoreService.selectedService.id,
								booking.id,
								extraItem.item_id,
								extraItem
							);
						}
					}
				}
			}
		} catch (err) {
			console.log(err);
		}

		if (this.datastoreService.selectedService.extra?.online_payment == 'Yes') {
			try {
				console.log(`Booking price: ${bookingPrice}`);
				// now we update the price of the booking
				await this.apiService.updatePrice(
					this.selectedLocation,
					booking.id,
					purchase.id,
					`${bookingPrice * 100}`,
					'0'
				);

				localStorage.setItem('booking', JSON.stringify(booking));
				// set the resource, the date time and the location, the booking all on localstorage
				// localStorage.setItem('resource', JSON.stringify(this.selectedResourceObj));
				localStorage.setItem('date', JSON.stringify(this.selectedSlot));
				localStorage.setItem('location', JSON.stringify(this.selectedLocation));
				localStorage.setItem('booking', JSON.stringify(booking));
				localStorage.setItem('service', JSON.stringify(this.selectedService));

				const authToken: string = localStorage.getItem('auth_token');
				console.log('authToken', authToken);
				const extra: any =
					await this.apiService.getServiceItemExtraLinksForBooking(
						booking.company_id,
						booking.id,
						authToken,
						this.member.id
					);
				console.log(extra);
				let payCallNote = '';
				let note = `Payment type: `;
				let hasDeposit = false;
				if (extra && extra.length > 0) {
					for (const item of extra) {
						if (
							item.item_id !==
							this.datastoreService.selectedService.extra?.fees_extra_item_id
						) {
							if (item.data && item.data.quantity) {
								certs = parseInt(item.data?.quantity);
							}
							break;
						}
					}
					for (const item of extra) {
						if (
							item.item_id ===
							this.datastoreService.selectedService.extra?.fees_extra_item_id
						) {
							// now we know the deposit and full fee value
							if (item.data.deposit !== item.data.cost) {
								const daysAway = moment(booking.datetime).diff(moment(), 'd');
								if (daysAway > 182) {
									note = `${note} Ceremony Fee Deposit`;
									payCallNote = `Ceremony Fee Deposit`;
									if (certs > 0) {
										note = `${note} & Certificates in Full`;
										payCallNote = `${payCallNote} & Certificates in Full`;
									}
									hasDeposit = true;
								} else {
									note = `${note} Ceremony Fee`;
									payCallNote = `Ceremony Fee`;
									if (certs > 0) {
										note = `${note} & Certificates in Full`;
										payCallNote = `${payCallNote} & Certificates in Full`;
									}
								}
							} else {
								note = `${note} Ceremony Fee`;
								payCallNote = `Ceremony Fee`;
								if (certs > 0) {
									note = `${note} & Certificates in Full`;
									payCallNote = `${payCallNote} & Certificates in Full`;
								}
							}
						}
					}
				}
				// now update the note
				note += ` | Deposit? ${hasDeposit ? 'Yes' : 'No'}`;
				if (certs > 0) {
					note += ` | Number of certificates: ${certs}`;
				}
				note += ` | Deposit payment date: ${moment()
					.tz('Europe/London')
					.format('dddd, MMMM Do YYYY')}`;
				try {
					// JRNI refuses a £0 payment with a 400, which shows the error banner.
					if (bookingPrice > 0) {
						await this.apiService.payPurchase(
							this.selectedLocation.id,
							booking.service_id,
							booking.purchase_id,
							{
								amount: bookingPrice,
								transaction_id: ``,
								notify: false,
								notify_admin: false,
								payment_status: 'paid',
								notes: `${payCallNote}`,
							},
							booking._embedded.client.id
						);
					}
				} catch (err) {
					console.error(err);
				}

				try {
					await this.apiService
						.addNoteToBooking(this.selectedLocation, booking.id, note)
						.then(async (body: any) => {
							// Loop over booking.questions and attach images for base64 answers
							for (const key in body.questions) {
								if (body.questions.hasOwnProperty(key)) {
									const question = body.questions[key];

									if (
										question.answer &&
										typeof question.answer === 'string' &&
										UPLOADED_FILE_ANSWER.test(question.answer)
									) {
										try {
											await this.basketService.attachImageToBooking(
												body.purchase_ref,
												body.id,
												question.answer
											);
											console.log(
												`Attached image for question: ${question.name}`
											);
										} catch (error) {
											console.error(
												`Error attaching image for question: ${question.name}`,
												error
											);
										}
									}
								}
							}
						});
				} catch (err) {
					console.error(err);
				}

				try {
					const config: any = this.config;
					if (
						config &&
						config.journey_config &&
						config.journey_config.hasOwnProperty('crm_push')
					) {
						if (config.journey_config.crm_push === 'osc') {
							await this.apiService.submitCaseToOracle(
								this.selectedDepartment,
								booking
							);
						}
					}
				} catch (err) {
					console.error(err);
				}

				try {
					const dontSendIds =
						this.config.journey_config.welcome_email_ignore_list;
					// dont send emails for these ids
					if (
						!dontSendIds.includes(
							this.datastoreService.selectedService.global_id
						)
					) {
						// if (!this.datastoreService.isCitizenshipRoute) {
						// 	await this.apiService.sendWelcomeEmail(
						// 		this.selectedLocation.id,
						// 		member.id
						// 	);
						// }
						// await this.apiService.bookingTimeline(
						// 	this.selectedLocation.id,
						// 	booking.purchase_id,
						// 	member.id,
						// 	member.email
						// );
					}
				} catch (err) {
					console.error(err);
				}

				this.spinner.hide();
				this.router.navigate(['confirmation']);
				return;
			} catch (err) {
				console.error(err);
				this.spinner.hide();
				// this.apiService.logPaymentExtra(
				//   this.company,
				//   this.dataStore.selectedService,
				//   booking.purchase_id,
				//   {
				//     "purchase": booking.purchase_id,
				//     "payment_error": err
				//   }
				// );
				this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
				this.router.navigate(['locations']);
			}
		}
	}

	async afterCheckout(res) {
		console.log(res);
		const purchase: any = await this.apiService.findPurchase(
			this.selectedLocation,
			res.id
		);
		console.log(purchase);

		const booking = purchase._embedded.bookings[0];
		this.datastoreService.booking = booking;
		this.datastoreService.purchase = purchase;
		//
		console.log(booking);

		// await this.apiService.sendFrontSheetEmail(booking); // sendCommunicationEmail
		let ceremonyFee;
		try {
			if (this.datastoreService.serviceFees.length > 0) {
				// find ceremony base cost
				const noQuestionId = this.datastoreService.serviceFees.find(
					(item) => item.questionid == undefined
				);
				console.log('noQuestionId', noQuestionId);
				if (noQuestionId) {
					ceremonyFee = noQuestionId;
					// Leave the base cost in serviceFees - the fee table renders that
					// array, and the extra-item loop below skips rows with no questionid.
				}
			}

			// ceremonyFee = this.datastoreService.serviceFees.shift();
		} catch (err) {
			console.log(err);
		}
		console.log(
			'this.datastoreService.adminJourney',
			this.datastoreService.adminJourney,
			'this.datastoreService.selectedService.extra?.online_payment',
			this.datastoreService.selectedService.extra?.online_payment,
			'this.datastoreService.serviceFees',
			this.datastoreService.serviceFees,
			'ceremonyFee',
			ceremonyFee
		);
		let paymentAmount = 0;
		let bookingPrice = 0;
		let chargePrice = 0;
		let certs = 0;
		const foundExtraItemIds = [];
		const foundExtraItemCharges = [];

		let certsToRemoveCount = 0;
		if (this.adminJourney) {
			const certsToRemove = this.adminForm.controls['certificate_removal'];
			if (parseInt(certsToRemove.value) > 0) {
				console.log(`We should remove ${certsToRemove.value} certificates`);
				certsToRemoveCount = parseInt(certsToRemove.value);
			}
		}

		try {
			// link booking to extra item ONLY IF we are not an admin journey, but we always want to calculate the price.
			if (this.datastoreService.config.hasOwnProperty('journey_config')) {
				if (
					this.datastoreService.config.journey_config.hasOwnProperty(
						'ceremonies'
					)
				) {
					// if (this.datastoreService.config.journey_config.ceremonies.hasOwnProperty('fees_extra_item_id')) {
					//   const extraItem = this.datastoreService.config.journey_config.ceremonies.fees_extra_item_id.trim();
					//   await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem, ceremonyFee);
					// }
					if (this.datastoreService.selectedService.extra?.fees_extra_item_id) {
						const extraItem =
							this.datastoreService.selectedService.extra?.fees_extra_item_id.trim();
						if (extraItem && extraItem.length > 0) {
							// paymentAmount += ceremonyFee.deposit;
							// paymentAmount = this.datastoreService.totalCost;
							bookingPrice += ceremonyFee.cost;
							// if (!this.adminJourney) {
							await this.apiService.linkExtraItem(
								this.selectedLocation.id,
								this.datastoreService.selectedService.id,
								booking.id,
								extraItem,
								ceremonyFee
							);
							// }
						}
					}
					// now link other items
					console.log('serviceFees', this.datastoreService.serviceFees);
					for (const extraItem of this.datastoreService.serviceFees) {
						if (extraItem.questionid) {
							const question = this.datastoreService.companyQuestions.find(
								(q) => q.id == extraItem.questionid
							);
							if (question) {
								if (question.help_text) {
									const schema: any = JSON.parse(question.help_text);
									if (schema['extra-item']) {
										const extraItemId = schema['extra-item'];
										foundExtraItemIds.push(extraItemId);
										foundExtraItemCharges.push(
											(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0)
										);
										// if (question.id == this.datastoreService.config.journey_config.ceremonies.included_certificates_question) {
										// paymentAmount += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
										// bookingPrice += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
										// chargePrice += Number(extraItem.cost) || 0;
										bookingPrice +=
											(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
										// bookingPrice += extraItem.cost;
										// }
										certs++;
										// Always link extra items
										// if (!this.adminJourney) {
										await this.apiService.linkExtraItem(
											this.selectedLocation.id,
											this.datastoreService.selectedService.id,
											booking.id,
											extraItemId,
											extraItem
										);
										// }
									} else {
										console.log('Missing extra item');
									}
								} else {
									console.log('Missing schema');
								}
							} else {
								console.log(`Cant find question ${extraItem.questionid}`);
								chargePrice += Number(extraItem.cost) || 0;
								bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
								await this.apiService.linkExtraItem(
									this.selectedLocation.id,
									this.datastoreService.selectedService.id,
									booking.id,
									extraItem.item_id,
									extraItem
								);
							}
						} else {
							console.log('No question ID on extra item');
							chargePrice += Number(extraItem.cost) || 0;
							bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
							await this.apiService.linkExtraItem(
								this.selectedLocation.id,
								this.datastoreService.selectedService.id,
								booking.id,
								extraItem.item_id,
								extraItem
							);
						}
					}
				}
			}
		} catch (err) {
			console.log(err);
		}

		if (
			!this.datastoreService.adminJourney &&
			this.datastoreService.selectedService.extra?.online_payment == 'Yes' &&
			ceremonyFee &&
			ceremonyFee.hasOwnProperty('deposit')
		) {
			try {
				console.log(`Booking price: ${bookingPrice}`);
				// now we update the price of the booking
				await this.apiService.updatePrice(
					this.selectedLocation,
					booking.id,
					purchase.id,
					`${bookingPrice * 100}`,
					'0'
				);

				localStorage.setItem('booking', JSON.stringify(booking));
				// set the resource, the date time and the location, the booking all on localstorage
				localStorage.setItem(
					'resource',
					JSON.stringify(this.selectedResourceObj)
				);
				localStorage.setItem('date', JSON.stringify(this.selectedSlot));
				localStorage.setItem('location', JSON.stringify(this.selectedLocation));
				localStorage.setItem('booking', JSON.stringify(booking));
				localStorage.setItem('service', JSON.stringify(this.selectedService));
				const extraItemId =
					this.datastoreService.selectedService.extra?.fees_extra_item_id.trim();
				let extraItem;
				const extraItemBonus = [];
				try {
					extraItem = await this.apiService.getExtraItem(
						booking.company_id,
						this.selectedService.global_id,
						extraItemId
					);
					for (let i in foundExtraItemIds) {
						res = await this.apiService.getExtraItem(
							booking.company_id,
							this.selectedService.global_id,
							foundExtraItemIds[i]
						);
						if (res) {
							res.chargePrice = foundExtraItemCharges[i];
							extraItemBonus.push(res);
						}
					}
				} catch (err) {
					console.error(err);
				}
				console.log('extraItems');
				console.log(extraItem);
				console.log(extraItemBonus);
				// const redir = await this.capitaService.invoke(
				//   this.selectedLocation,
				//   booking,
				//   purchase,
				//   this.member,
				//   this.selectedService.name,
				//   (extraItem) ? extraItem.data.gl_code : '',
				//   (this.datastoreService.totalCost * 100), // (paymentAmount * 100),
				//   false,
				//   (extraItem) ? extraItem.data.short_code : '',
				//   extraItemBonus
				// );
				// if (redir && redir["invoke_status"] === "SUCCESS") {
				//   localStorage.setItem("scp", redir["scp"]);
				//   window.location = redir["url"];
				// }
			} catch (err) {
				console.error(err);
				// this.apiService.logPaymentExtra(
				//   this.selectedLocation,
				//   this.selectedService,
				//   purchase.id,
				//   {
				//     "purchase": purchase,
				//     "invoke_error": err
				//   }
				// );
				this.basketService.deleteAllBaskets(this.selectedLocation);
				this.spinner.hide();
				this.checkoutStarted = false;
			}
		} else {
			// update booking price
			// let questionPrice = 0
			// for (const q of booking._embedded.answers) {
			//   if (q.question_id == 34666) {
			//     if (q.value > 0) {
			//       questionPrice = q.value
			//       const newPrice = (questionPrice * 100) + booking.price;
			//       await this.apiService.updatePrice(this.selectedLocation, booking.id, booking.purchase_id, newPrice, booking.paid);
			//     }
			//   }
			// }
			// if (this.datastoreService.config.hasOwnProperty('journey_config')) {
			//   if (this.datastoreService.config.journey_config.hasOwnProperty('ceremonies')) {
			//     if (this.datastoreService.config.journey_config.ceremonies.hasOwnProperty('included_certificates_question')) {
			//       const includedCerts = this.datastoreService.config.journey_config.ceremonies.included_certificates_question;
			//       if (this.datastoreService.config.journey_config.ceremonies.included_certificates_question.length > 0) {
			//         let certsToAdd = 0;
			//         for (const extraItem of this.datastoreService.serviceFees) {
			//           if (extraItem.questionid) {
			//             const question = this.datastoreService.companyQuestions.find((q) => q.id == extraItem.questionid);
			//             // Only do this for initial included certificates
			//             console.log(includedCerts);
			//             console.log('question', question);
			//             const foundItem = includedCerts.find((q:any) => q.question_id == question.id);
			//             console.log('foundItem', foundItem);
			//             if (foundItem) {
			//               if (question && question.id === foundItem.question_id) {
			//                 if (question.help_text) {
			//                   const schema: any = JSON.parse(question.help_text);
			//                   if (schema['extra-item']) {
			//                     const extraItemId = schema['extra-item'];
			//                     if (!foundExtraItemId) {
			//                       foundExtraItemId = extraItemId;
			//                     }
			//                     console.log(extraItem);
			//                     let itemQty;
			//                     if (extraItem.quantity) {
			//                       itemQty = parseInt(extraItem.quantity);
			//                       if (certsToRemoveCount > 0) {
			//                         itemQty -= certsToRemoveCount;
			//                         if (itemQty < 0) {
			//                           itemQty = 0;
			//                         }
			//                       }

			//                       if (itemQty > 0) {
			//                         console.log('options', question.options, question.options.length-1, certsToRemoveCount, question.options[(question.options.length-1) - itemQty]);
			//                         console.log(bookingPrice);
			//                         bookingPrice -= (question.options[(question.options.length-1) - itemQty].price / 100);
			//                         console.log(bookingPrice);
			//                         extraItem.quantity = certsToRemoveCount;
			//                         // certs += certsToRemoveCount;
			//                         await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItemId, extraItem, 'subtract');
			//                       }
			//                       if (itemQty === 0) {
			//                         bookingPrice -= (question.options[question.options.length-1].price / 100);
			//                       }
			//                     }
			//                     // paymentAmount += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
			//                     // bookingPrice += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));

			//                   }
			//                   else {
			//                     console.log('Missing extra item');
			//                   }
			//                 }
			//                 else {
			//                   console.log('Missing schema');
			//                 }

			//               }
			//               else {
			//                 console.log(`Cant find question ${extraItem.questionid}`);
			//               }
			//             }

			//           }
			//           else {
			//             console.log('No question ID on extra item');
			//           }
			//         }
			//       }
			//     }
			//   }

			// }
			// now we update the price of the booking
			const extraItems = [];
			if (foundExtraItemIds) {
				for (const foundExtraItemId of foundExtraItemIds) {
					const extraItem = await this.apiService.getExtraItem(
						booking.company_id,
						this.datastoreService.selectedService.global_id,
						foundExtraItemId
					);
					extraItems.push(extraItem);
				}
			}
			let note = `Payment type: `;
			let payCallNote = '';
			let hasDeposit = false;
			const authToken: string = localStorage.getItem('auth_token');
			console.log('authToken', authToken);
			const extra: any =
				await this.apiService.getServiceItemExtraLinksForBooking(
					booking.company_id,
					booking.id,
					authToken,
					this.member.id
				);

			if (extra && extra.length > 0) {
				for (const item of extra) {
					if (
						item.item_id !==
						this.datastoreService.selectedService.extra?.fees_extra_item_id
					) {
						if (item.data && item.data.quantity) {
							certs = parseInt(item.data?.quantity);
						}
						break;
					}
				}
				for (const item of extra) {
					if (
						item.item_id ===
						this.datastoreService.selectedService.extra?.fees_extra_item_id
					) {
						// now we know the deposit and full fee value
						if (item.data.deposit !== item.data.cost) {
							// const ceremonyDate = moment(this.booking.datetime);
							const daysAway = moment(booking.datetime).diff(moment(), 'd');
							if (daysAway > 182) {
								note = `${note} Ceremony Fee Deposit`;
								payCallNote = `Ceremony Fee Deposit`;
								if (certs > 0) {
									note = `${note} & Certificates in Full`;
									payCallNote = `${payCallNote} & Certificates in Full`;
								}
								hasDeposit = true;
							} else {
								note = `${note} Ceremony Fee`;
								payCallNote = `Ceremony Fee`;
								if (certs > 0) {
									note = `${note} & Certificates in Full`;
									payCallNote = `${payCallNote} & Certificates in Full`;
								}
							}
						} else {
							note = `${note} Ceremony Fee`;
							payCallNote = `Ceremony Fee`;
							if (certs > 0) {
								note = `${note} & Certificates in Full`;
								payCallNote = `${payCallNote} & Certificates in Full`;
							}
						}
					}
				}
			}

			note = `${note} ${this.form.note.value}`;
			payCallNote = `${this.form.note.value}`;

			note += ` | Deposit? ${hasDeposit ? 'Yes' : 'No'}`;
			if (certs > 0 && extraItems) {
				for (const extraItem of extraItems) {
					note += `| Selected available extra: ${extraItem.item_name}`;
				}
				note += ` | Quantity: ${certs}`;
			}
			note += ` | Deposit payment date: ${moment()
				.tz('Europe/London')
				.format('dddd, MMMM Do YYYY')}`;
			note += ` | Deposit payment amount: £${parseFloat(
				this.form.amount.value
			)}`;
			note += ` | Deposit receipt number: ${this.form.reference.value}`;

			console.log(this.form);

			try {
				await this.apiService.updatePrice(
					this.selectedLocation,
					booking.id,
					booking.purchase_id,
					`${bookingPrice * 100}`,
					`0`
				);
				await this.apiService.payPurchase(
					this.selectedLocation.id,
					booking.service_id,
					booking.purchase_id,
					{
						amount: parseFloat(this.form.amount.value),
						transaction_id: `${this.form.reference.value}`,
						notify: false,
						notify_admin: false,
						payment_status: 'paid',
						notes: `${payCallNote}`,
					},
					booking._embedded.client.id
				);
				await this.apiService
					.addNoteToBooking(this.selectedLocation, booking.id, note)
					.then(async (body: any) => {
						// Loop over booking.questions and attach images for base64 answers
						for (const key in body.questions) {
							if (body.questions.hasOwnProperty(key)) {
								const question = body.questions[key];

								if (
									question.answer &&
									typeof question.answer === 'string' &&
									UPLOADED_FILE_ANSWER.test(question.answer)
								) {
									try {
										await this.basketService.attachImageToBooking(
											body.purchase_ref,
											body.id,
											question.answer
										);
										console.log(
											`Attached image for question: ${question.name}`
										);
									} catch (error) {
										console.error(
											`Error attaching image for question: ${question.name}`,
											error
										);
									}
								}
							}
						}
					});
			} catch (err) {
				console.error(err);
			}

			if (this.datastoreService.clientCreated) {
				this.datastoreService.clientCreated = false;
				try {
					// if (!this.datastoreService.isCitizenshipRoute) {
					// 	await this.apiService.sendWelcomeEmail(
					// 		this.selectedLocation.id,
					// 		this.member.id
					// 	);
					// }
					// await this.apiService.bookingTimeline(
					// 	this.selectedLocation.id,
					// 	booking.purchase_id,
					// 	this.member.id,
					// 	this.member.email
					// );
				} catch (err) {
					console.error(err);
				}
			}
			// set the resource, the date time and the location, the booking all on localstorage
			localStorage.setItem(
				'resource',
				JSON.stringify(this.selectedResourceObj)
			);
			localStorage.setItem('date', JSON.stringify(this.selectedSlot));
			localStorage.setItem('location', JSON.stringify(this.selectedLocation));
			localStorage.setItem('booking', JSON.stringify(booking));
			localStorage.setItem('service', JSON.stringify(this.selectedService));
			this.router.navigate(['/confirmation']);
		}
	}

	async telephonePayment() {
		this.checkoutStarted = true;
		this.spinner.show();

		// Check if selected slot is still available
		let slotAvailable: boolean;

		await this.apiService
			.checkSlotAvailability(this.selectedDepartment, this.selectedSlot.start)
			.then((res: any) => {
				slotAvailable = res.available;
			});

		if (slotAvailable === false) {
			// If unavailable, then alert user
			this.alertService.error(
				this.translateService.instant('SUMMARY.INVALID_SLOT')
			);
			this.scrollToTop();
			this.slotAvailable = false;
			this.spinner.hide();
			this.checkoutStarted = false;
		} else {
			// If available then checkout
			// this.checkout()
			try {
				const priceData = await this.preCheckoutExtras();
				console.log('priceData', priceData);
				await this.preBasketPayment(priceData.paymentAmount, true);
			} catch (err) {
				console.error(err);
			}
		}
	}

	async preCheckoutExtras() {
		try {
			if (this.datastoreService.serviceFees.length > 0) {
				// find ceremony base cost
				const noQuestionId = this.datastoreService.serviceFees.find(
					(item) => item.questionid == undefined
				);
				console.log('noQuestionId', noQuestionId);
				if (noQuestionId) {
					this.ceremonyFee = noQuestionId;
					// Leave the base cost in serviceFees - the fee table renders that
					// array, and the extra-item loop below skips rows with no questionid.
				}
			}

			// ceremonyFee = this.datastoreService.serviceFees.shift();
		} catch (err) {
			console.log(err);
		}
		console.log(
			'this.datastoreService.adminJourney',
			this.datastoreService.adminJourney,
			'this.datastoreService.selectedService.extra?.online_payment',
			this.datastoreService.selectedService.extra?.online_payment,
			'this.datastoreService.serviceFees',
			this.datastoreService.serviceFees,
			'ceremonyFee',
			this.ceremonyFee
		);
		let paymentAmount = 0;
		let bookingPrice = 0;
		let chargePrice = 0;
		let certs = 0;
		let foundExtraItemIds = [];
		let foundExtraItemCharges = [];

		let certsToRemoveCount = 0;
		if (this.adminJourney) {
			const certsToRemove = this.adminForm.controls['certificate_removal'];
			if (parseInt(certsToRemove.value) > 0) {
				console.log(`We should remove ${certsToRemove.value} certificates`);
				certsToRemoveCount = parseInt(certsToRemove.value);
			}
		}

		if (!this.datastoreService.isCitizenshipRoute) {
			try {
				// link booking to extra item ONLY IF we are not an admin journey, but we always want to calculate the price.
				if (this.datastoreService.config.hasOwnProperty('journey_config')) {
					if (
						this.datastoreService.config.journey_config.hasOwnProperty(
							'appointments'
						)
					) {
						// if (this.datastoreService.config.journey_config.ceremonies.hasOwnProperty('fees_extra_item_id')) {
						//   const extraItem = this.datastoreService.config.journey_config.ceremonies.fees_extra_item_id.trim();
						//   await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem, ceremonyFee);
						// }
						if (isNaN(this.datastoreService.totalCost)) {
							this.datastoreService.totalCost = 0;
						}
						if (this.datastoreService.selectedService.extra?.fees_extra_item_id) {
							const extraItem =
								this.datastoreService.selectedService.extra?.fees_extra_item_id.trim();
							if (extraItem && extraItem.length > 0) {
								// paymentAmount += ceremonyFee.deposit;
								console.log('paymentAmount', paymentAmount);
								// paymentAmount = this.datastoreService.totalCost;
								if (isNaN(paymentAmount)) {
									paymentAmount = 0;
								}
								if (isNaN(this.ceremonyFee.cost)) {
									this.ceremonyFee.cost = 0;
								}
								console.log('this.ceremonyFee.cost', this.ceremonyFee.cost);
								bookingPrice += this.ceremonyFee.cost;
								console.log('bookingPrice', bookingPrice);
								this.bookingPrice = bookingPrice;
								// if (!this.adminJourney) {
								// await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem, ceremonyFee);
								// }
							}
						}
						// now link other items
						console.log('serviceFees', this.datastoreService.serviceFees);
						for (const extraItem of this.datastoreService.serviceFees) {
							if (extraItem.questionid) {
								const question = this.datastoreService.companyQuestions.find(
									(q) => q.id == extraItem.questionid
								);
								if (question) {
									if (question.help_text) {
										const schema: any = JSON.parse(question.help_text);
										if (schema['extra-item']) {
											const extraItemId = schema['extra-item'];
											foundExtraItemIds.push(extraItemId);
											foundExtraItemCharges.push(
												(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0)
											);
											// if (question.id == this.datastoreService.config.journey_config.ceremonies.included_certificates_question) {
											// paymentAmount += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
											// bookingPrice += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
											// chargePrice += Number(extraItem.cost) || 0;
											bookingPrice +=
												(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
											// bookingPrice += extraItem.cost;
											console.log('ei bookingPrice', bookingPrice);
											paymentAmount +=
												(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
											console.log('ei paymentAmount', paymentAmount);
											// }
											certs++;
											// Always link extra items
											// if (!this.adminJourney) {
											// await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItemId, extraItem);
											// }
										} else {
											console.log('Missing extra item');
										}
									} else {
										console.log('Missing schema');
									}
								} else {
									console.log(`Cant find question ${extraItem.questionid}`);
									chargePrice += Number(extraItem.cost) || 0;
									bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
									// await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem.item_id, extraItem);
								}
							} else {
								console.log('No question ID on extra item');
								chargePrice += Number(extraItem.cost) || 0;
								bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
								// await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem.item_id, extraItem);
							}
						}
					}
				}
				return {
					paymentAmount,
					bookingPrice,
				};
			} catch (err) {
				console.log(err);
			}
		}
		else {
			// citizenship
			try {
				this.ceremonyFee.item_id = this.datastoreService.citizenshipExtraItem.item_id;
				console.log('totalCost', this.datastoreService.totalCost);
				if (isNaN(this.datastoreService.totalCost)) {
					this.datastoreService.totalCost = 0;
				}

				if (isNaN(paymentAmount)) {
					paymentAmount = 0;
				}
				if (isNaN(this.ceremonyFee.cost)) {
					this.ceremonyFee.cost = 0;
				}
				console.log('this.ceremonyFee.cost', this.ceremonyFee.cost);
				bookingPrice += this.ceremonyFee.cost;
				console.log('bookingPrice', bookingPrice);
				this.bookingPrice = bookingPrice;

				// now link other items

				console.log('serviceFees', this.datastoreService.serviceFees);
				for (const extraItem of this.datastoreService.serviceFees) {
					if (extraItem.questionid) {
						const question = this.datastoreService.companyQuestions.find(
							(q) => q.id == extraItem.questionid
						);
						if (question) {
							if (question.help_text) {
								const schema: any = JSON.parse(question.help_text);
								if (schema['extra-item']) {
									const extraItemId = schema['extra-item'];
									foundExtraItemIds.push(extraItemId);
									foundExtraItemCharges.push(
										(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0)
									);
									// if (question.id == this.datastoreService.config.journey_config.ceremonies.included_certificates_question) {
									// paymentAmount += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
									// bookingPrice += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
									// chargePrice += Number(extraItem.cost) || 0;
									bookingPrice +=
										(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
									// bookingPrice += extraItem.cost;
									console.log('ei bookingPrice', bookingPrice);
									paymentAmount +=
										(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
									console.log('ei paymentAmount', paymentAmount);
									// }
									certs++;
									// Always link extra items
									// if (!this.adminJourney) {
									// await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItemId, extraItem);
									// }
								} else {
									console.log('Missing extra item');
								}
							} else {
								console.log('Missing schema');
							}
						} else {
							console.log(`Cant find question ${extraItem.questionid}`);
							chargePrice += Number(extraItem.cost) || 0;
							bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
							// await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem.item_id, extraItem);
						}
					} else {
						console.log('No question ID on extra item');
						chargePrice += Number(extraItem.cost) || 0;
						bookingPrice += (Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
						// await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem.item_id, extraItem);
					}
				}
				return {
					paymentAmount,
					bookingPrice,
				};
			}
			catch (err) {
				console.error(err);
			}
		}
	}

	// Recovery for a pre-payment gate block (409): the basket the journey built
	// is unusable under the current session (empty, or bound to a session we no
	// longer hold), so retrying the same basket can only be blocked again.
	// Rebuild deliberately and SEQUENTIALLY — delete stale baskets, create a
	// fresh one under the current session, re-add the service item — and return
	// the fresh pair for a single re-invoke. Throws if the slot can no longer be
	// re-added (taken/expired); the caller then shows the not-charged message.
	private async rebuildBasketForRecovery(): Promise<{ basket: any; serviceItem: any }> {
		if (!this.selectedSlot?.start || !this.selectedService) {
			throw new Error('Recovery: no slot/service state to rebuild from');
		}
		try {
			await this.apiService.deleteBaskets(this.selectedLocation);
		} catch (err) {
			// Stale-basket cleanup is best effort — creating the new basket is what matters.
			console.error('Recovery: could not delete stale baskets (continuing)', err);
		}
		const response: any = await this.apiService
			.createBasket(this.selectedLocation)
			.toPromise();
		const newBasket = response?.body;
		if (!newBasket || !newBasket.id) {
			throw new Error('Recovery: basket could not be re-created');
		}
		const newServiceItem: any = await this.apiService.addServiceItem(
			this.selectedLocation,
			newBasket,
			this.selectedSlot.start,
			this.selectedService,
			this.bookingAnswers,
			this.selectedService.calculated_duration,
			this.selectedResource
		);
		// api.service swallows HTTP failures and returns the error message as a
		// STRING instead of throwing — anything that is not a service item object
		// means the re-add failed (slot may no longer be available).
		if (!newServiceItem || typeof newServiceItem !== 'object' || newServiceItem.id == null) {
			throw new Error(
				`Recovery: service item could not be re-added: ${JSON.stringify(newServiceItem)}`
			);
		}
		this.basketService.currentBasket = newBasket;
		this.basketService.currentServiceItem = newServiceItem;
		console.log('Recovery: rebuilt basket', newBasket.id, 'service item', newServiceItem.id);
		return { basket: newBasket, serviceItem: newServiceItem };
	}

	async preBasketPayment(amount, telephony) {
		// Resolve the PARENT company id (e.g. 37003 appointments, or citizenship parent for
		// citizenship routes) — NOT the child/location company — sent as company_id on the
		// pre-basket call. Falls back to null + warn.
		const parentCompanyId = (this.datastoreService.isCitizenshipRoute
			? AppConfig.settings.core.citizenship_company_id
			: AppConfig.settings.core.company_id) || null;
		if (!parentCompanyId) {
			console.warn('[preBasket] Parent company_id could not be resolved from config; sending company_id as null');
		}
		const tokenBeforeClient = localStorage.getItem('auth_token');
		const clientCreated = await this.createClient(this.adminJourney);
		// createClient can mint a NEW JRNI session (client create / SSO login both
		// rotate the auth token). Baskets are session-scoped: the basket built under
		// the old token does not exist for the new one, so reusing it can only 404 —
		// at best pre-charge, at worst post-payment as FAILED_CHECKOUT_FAILED
		// ("Basket was not found", RBKC 2026-09-01). Detect the rotation and force
		// addItem to build a fresh basket + item under the new session so the
		// (basket, auth_token) pair sent to the payment gateway always belongs to
		// one session and the post-payment checkout can find it.
		const sessionRotated = localStorage.getItem('auth_token') !== tokenBeforeClient;
		if (sessionRotated) {
			console.warn('[preBasket] JRNI session rotated during client resolution — rebuilding basket under the new session');
			this.basketService.currentBasket = null;
			this.basketService.currentServiceItem = null;
		}
		// One Pay click = ONE execution. These subscriptions used to be re-created on
		// every attempt and never torn down, and store/addedItem are BehaviorSubjects
		// (they replay their last value to every new subscriber). A retry click after
		// a failed attempt therefore spawned parallel executions over shared basket
		// state — one of which could hit an abort path and delete the session's
		// baskets while another minted a payment URL against them (Westminster
		// FAILED_CHECKOUT_FAILED incidents, 2026-08-31: money taken, basket already
		// deleted, booking never created). first() makes each subscription fire once
		// and auto-unsubscribe; addedItem is reset per attempt so a stale item from a
		// previous attempt can never replay into this one.
		this.addedItem.next(false);
		await new Promise<void>((resolve) => { this.store.asObservable().pipe(first()).subscribe(async (state) => {
			console.log('state', state);
			if (state) {
				console.log('client created');
				try {
					await this.addItem(sessionRotated);
				} catch (err) {
					// addItem() has already surfaced the error to the customer. Do not fall through:
					// the service item can still hold whatever resource JRNI auto-assigned, which is
					// exactly how bookings land on the wrong resource.
					console.error('Aborting checkout — service item update failed', err);
					this.checkoutStarted = false;
					this.spinner.hide();
					resolve();
					return;
				}
				this.addedItem.asObservable().pipe(first((item: any) => !!item)).subscribe(async (item) => {
					if (
						this.departmentService.selectedDepartment.hasOwnProperty('extra') &&
						this.departmentService.selectedDepartment.extra.hasOwnProperty(
							'login_required'
						)
					) {
						this.departmentService.selectedDepartment.extra.login_required.toLowerCase() ===
							'yes'
							? (this.member = await this.memberService.getMember())
							: (this.member = await this.datastoreService.client);
					} else {
						this.member = await this.datastoreService.client;
					}

					const currentBasket = await this.basketService.currentBasket;
					console.log('currentBasket', JSON.stringify(currentBasket));
					const serviceItem = this.basketService.currentServiceItem;
					console.log('serviceItem', serviceItem);

					// Guard: a transient basket / service-item API call can leave
					// currentServiceItem as an error-message string (api.service.addServiceItem
					// returns (error).message on failure) instead of a real service item.
					// Proceeding would build a broken pre-basket return URL ending in
					// ".../payment-verify/<company>/<basket>/,undefined". Fail safe instead.
					// Deliberately does NOT delete the session's baskets: this guard fires on
					// transient/stale state, and destroying the basket here is exactly what
					// turned a harmless abort into "payment taken, booking not created" when
					// another attempt had a payment URL minted against it.
					if (!serviceItem || typeof serviceItem !== 'object' || serviceItem.id == null) {
						console.error('Aborting payment: invalid service item (missing id)', serviceItem);
						this.checkoutStarted = false;
						this.checkoutFailed = true;
						this.spinner.hide();
						this.scrollToTop();
						resolve();
						return;
					}

					const authToken = localStorage.getItem('auth_token');

					// serviceFees still contains the base-cost row (questionid undefined) so the
					// fee table can render it; the ceremony fee is prepended explicitly, so the
					// copy must exclude the base row or it is sent twice.
					const tmpFees: any[] = JSON.parse(
						JSON.stringify(
							this.datastoreService.serviceFees.filter(
								(fee) => fee.questionid !== undefined
							)
						)
					);
					tmpFees.unshift(this.ceremonyFee);
					let journeyRoute = '';
					if (this.datastoreService.journeyRoute) {
						journeyRoute = this.datastoreService.journeyRoute;
					}
					if (this.datastoreService.isCitizenshipRoute) {
						journeyRoute = 'citizenship';
					}
					console.log('journeyRoute', journeyRoute);
					console.log('this.datastoreService.isCitizenshipRoute', this.datastoreService.isCitizenshipRoute);

					console.log(this.selectedService);
					console.log('amount', amount, 'bookingPrice', this.bookingPrice);
					if (amount === 0) {
						amount = amount + this.bookingPrice;
					}
					console.log('Amount + bookingprice = ', amount);

					if (amount > 0) {
						if (!telephony) {
							// Build revCodes, amounts and descriptions as parallel arrays
							const descriptions = [this.selectedService.name];
							const revCodes = [];
							const amounts = [];
							let extraItem: any;
							if (!this.datastoreService.isCitizenshipRoute) {
								extraItem = await this.apiService.getExtraItem(
									this.selectedDepartment.id,
									this.selectedService.id,
									this.selectedService.extra?.fees_extra_item_id
								);
							}
							else {
								extraItem = this.datastoreService.citizenshipExtraItem;
							}

							// Ceremony/appointment fee revCode + amount
							if (extraItem) {
								revCodes.push(extraItem.data.pmtTyp || '');
							} else {
								revCodes.push(
									this.selectedService.extra?.pmttyp || ''
								);
							}
							amounts.push(this.ceremonyFee ? this.ceremonyFee.cost : 0);

							// Service fee revCodes + amounts. The base-cost row (questionid
							// undefined) stays in serviceFees for the fee table but is already
							// pushed above as line 1 - skip it here or it is charged twice.
							for (const fee of this.datastoreService.serviceFees) {
								if (fee.questionid === undefined) {
									continue;
								}
								amounts.push(fee.cost * parseInt(fee.quantity || '1'));
								if (fee.item) {
									descriptions.push(fee.item);
								}

								// Look up the extra item for this fee to get its pmtTyp
								let feeExtraItemId = null;
								if (fee.item_id) {
									feeExtraItemId = fee.item_id;
								} else if (fee.questionid) {
									const question = this.datastoreService.companyQuestions?.find(
										(q) => q.id == fee.questionid
									);
									if (question && question.help_text) {
										try {
											const schema = JSON.parse(question.help_text);
											if (schema['extra-item']) {
												feeExtraItemId = schema['extra-item'];
											}
										} catch (err) {
											console.log('Error parsing question help_text', err);
										}
									}
								}

								if (feeExtraItemId) {
									try {
										const feeExtraItem: any = await this.apiService.getExtraItem(
											this.selectedDepartment.id,
											this.selectedService.id,
											feeExtraItemId
										);
										revCodes.push(feeExtraItem?.data?.pmtTyp || '');
									} catch (err) {
										console.log('Error fetching extra item for fee', fee, err);
										revCodes.push('');
									}
								} else {
									revCodes.push('');
								}
							}
							console.log('amounts array', amounts);
							console.log('revCodes array', revCodes);

              console.log('this.datastoreService.selectedAddress', this.datastoreService.selectedAddress);

							try {
								const redir: any = await this.heyCentricService.preBasketInvoke(
									this.selectedLocation,
									currentBasket,
									[serviceItem],
									descriptions,
									this.member,
									revCodes,
									amounts,
									authToken,
									{
										fees: tmpFees,
										firstAvailableSlot: this.datastoreService.firstSlotAnalytic ? this.datastoreService.firstSlotAnalytic : "Null",
										lookupResponses: this.datastoreService.lookupResponses,
										// Not in lookupResponses, so send it too (RWRSPI-1095).
										formSubmissionResponses: this.datastoreService.formSubmissionResponses,
										selectedAddress: this.datastoreService.selectedAddress
									},
									this.datastoreService.type_of_appointment,
									this.selectedSlot,
									this.datastoreService.extraDuration,
									journeyRoute,
									parentCompanyId
								);

								console.log('REDIR', redir);
								// RWRSPI-967: mark a HeyCentric payment in flight (value = booking company id) so a browser Back press on the gateway can be recovered in app.component.
								sessionStorage.setItem('heycentric_payment_pending', `${this.selectedLocation?.id ?? 'pending'}`);
								window.location = redir.url;
								// this.spinner.hide();
							} catch (err: any) {
								console.error('Error in preBasketInvoke', err);

								// Pre-payment gate block (409): the basket is unusable
								// (empty / not on this session). Nothing has been charged.
								// Retrying the same basket can only be blocked again, so
								// rebuild it under the current session and re-invoke ONCE.
								if (err.blocked && !this.gateRecoveryAttempted) {
									this.gateRecoveryAttempted = true;
									console.warn('Pre-payment gate blocked the basket — attempting automatic rebuild', err.reason);
									try {
										const rebuilt = await this.rebuildBasketForRecovery();
										const retryAuthToken = localStorage.getItem('auth_token');
										const retryRedir: any = await this.heyCentricService.preBasketInvoke(
											this.selectedLocation,
											rebuilt.basket,
											[rebuilt.serviceItem],
											descriptions,
											this.member,
											revCodes,
											amounts,
											retryAuthToken,
											{
												fees: tmpFees,
												firstAvailableSlot: this.datastoreService.firstSlotAnalytic ? this.datastoreService.firstSlotAnalytic : "Null",
												lookupResponses: this.datastoreService.lookupResponses,
										// Not in lookupResponses, so send it too (RWRSPI-1095).
										formSubmissionResponses: this.datastoreService.formSubmissionResponses,
												selectedAddress: this.datastoreService.selectedAddress
											},
											this.datastoreService.type_of_appointment,
											this.selectedSlot,
											this.datastoreService.extraDuration,
											journeyRoute,
											parentCompanyId
										);
										sessionStorage.setItem('heycentric_payment_pending', `${this.selectedLocation?.id ?? 'pending'}`);
										window.location = retryRedir.url;
										return;
									} catch (recoveryErr: any) {
										console.error('Pre-payment gate recovery failed', recoveryErr);
									}
								}
								if (err.blocked) {
									// Recovery already attempted (or just failed): tell the
									// customer plainly that they have NOT been charged.
									this.paymentBlockedUnrecoverable = true;
								}
								this.checkoutStarted = false;
								this.checkoutFailed = true;
								// Free up the slot — but ONLY this attempt's basket. A session-wide
								// delete here can destroy a basket another attempt/tab has a
								// payment URL minted against (post-payment checkout then 404s).
								const cancelled = await this.apiService.deleteBaskets(this.selectedLocation, currentBasket?.id);
								if (!cancelled) {
									console.error('Failed to clear basket after payment failure');
								}
								this.spinner.hide();
								this.scrollToTop();
							}
						} else {
							// Build revCodes, amounts and descriptions as parallel arrays
							const descriptions = [this.selectedService.name];
							const revCodes = [];
							const amounts = [];
							let extraItem: any;
							if (!this.datastoreService.isCitizenshipRoute) {
								extraItem = await this.apiService.getExtraItem(
									this.selectedDepartment.id,
									this.selectedService.id,
									this.selectedService.extra?.fees_extra_item_id
								);
							}
							else {
								extraItem = this.datastoreService.citizenshipExtraItem;
							}

							// Appointment fee revCode + amount
							if (extraItem) {
								revCodes.push(extraItem.data.pmtTyp || '');
							} else {
								revCodes.push(
									this.selectedService.extra?.pmttyp || ''
								);
							}
							amounts.push(this.ceremonyFee ? this.ceremonyFee.cost : 0);

							// Service fee revCodes + amounts. The base-cost row (questionid
							// undefined) stays in serviceFees for the fee table but is already
							// pushed above as line 1 - skip it here or it is charged twice.
							for (const fee of this.datastoreService.serviceFees) {
								if (fee.questionid === undefined) {
									continue;
								}
								amounts.push(fee.cost * parseInt(fee.quantity || '1'));
								if (fee.item) {
									descriptions.push(fee.item);
								}

								// Look up the extra item for this fee to get its pmtTyp
								let feeExtraItemId = null;
								if (fee.item_id) {
									feeExtraItemId = fee.item_id;
								} else if (fee.questionid) {
									const question = this.datastoreService.companyQuestions?.find(
										(q) => q.id == fee.questionid
									);
									if (question && question.help_text) {
										try {
											const schema = JSON.parse(question.help_text);
											if (schema['extra-item']) {
												feeExtraItemId = schema['extra-item'];
											}
										} catch (err) {
											console.log('Error parsing question help_text', err);
										}
									}
								}

								if (feeExtraItemId) {
									try {
										const feeExtraItem: any = await this.apiService.getExtraItem(
											this.selectedDepartment.id,
											this.selectedService.id,
											feeExtraItemId
										);
										revCodes.push(feeExtraItem?.data?.pmtTyp || '');
									} catch (err) {
										console.log('Error fetching extra item for fee', fee, err);
										revCodes.push('');
									}
								} else {
									revCodes.push('');
								}
							}
							console.log('amounts array', amounts);
							console.log('revCodes array', revCodes);

							// get the admin user first
							// const adminUser = admins._embedded.administrators[0];
							let userRole = 'Administrator';
							let adminUser;
							let adminPhone;
							let userId;
							try {
								adminUser = JSON.parse(localStorage.getItem('agent'));
								console.log('adminUser', adminUser);
								if (adminUser) {
									const adminProfile: any = JSON.parse(
										localStorage.getItem('agent_profile')
									);
									userId = adminProfile.mail.split('@')[0];

									let userRole = adminUser.role;
									if (userRole === 'callcenter') {
										userRole = 'agent';
									} else {
										userRole = 'admin';
									}
								}
								else {
									const adminTmp: any = JSON.parse(localStorage.getItem('admin_user'));
									adminUser = {
										id: adminTmp.email,
										email: adminTmp.email
									};
									userRole = 'admin';
								}
							}
							catch (err) {
								userRole = 'admin';
							}
							if (
								this.selectedDepartment.extra[`hunt_group_number_${userRole}`]
							) {
								adminPhone =
									this.selectedDepartment.extra[
									`hunt_group_number_${userRole}`
									];
							}
							console.log('adminUser', adminUser);

							let journeyRoute = '';
							if (this.datastoreService.journeyRoute) {
								journeyRoute = this.datastoreService.journeyRoute;
							}
							if (this.datastoreService.isCitizenshipRoute) {
								journeyRoute = 'citizenship';
							}

							try {
								const redir: any =
									await this.heyCentricService.preBasketTelephonyInvoke(
										this.selectedLocation,
										currentBasket,
										[serviceItem],
										descriptions,
										this.member,
										revCodes,
										amounts,
										authToken,
										{
											fees: tmpFees,
											firstAvailableSlot: this.datastoreService.firstSlotAnalytic ? this.datastoreService.firstSlotAnalytic : "Null",
											lookupResponses: this.datastoreService.lookupResponses,
										// Not in lookupResponses, so send it too (RWRSPI-1095).
										formSubmissionResponses: this.datastoreService.formSubmissionResponses,
											selectedAddress: this.datastoreService.selectedAddress
										},
										this.datastoreService.type_of_appointment,
										this.selectedSlot,
										this.datastoreService.extraDuration,
										'M2',
										userId,
										adminUser.email,
										"03334440203",
										journeyRoute,
										parentCompanyId
									);

								console.log('REDIR', redir);
								// RWRSPI-967: mark a HeyCentric payment in flight (value = booking company id) so a browser Back press on the gateway can be recovered in app.component.
								sessionStorage.setItem('heycentric_payment_pending', `${this.selectedLocation?.id ?? 'pending'}`);
								window.location = redir.url;
								// this.spinner.hide();
							} catch (err: any) {
								console.error('Error in preBasketTelephonyInvoke', err);
								if (err.blocked) {
									// Pre-payment gate 409 on the agent (ARP) path: the basket
									// is unusable and retrying it is futile. No auto-rebuild
									// here — the agent restarts; nothing was charged.
									this.paymentBlockedUnrecoverable = true;
								}
								this.checkoutStarted = false;
								this.checkoutFailed = true;
								// Free up the slot — but ONLY this attempt's basket (see the
								// non-telephony catch above for why a session-wide delete is unsafe).
								const cancelled = await this.apiService.deleteBaskets(this.selectedLocation, currentBasket?.id);
								if (!cancelled) {
									console.error('Failed to clear basket after payment failure');
								}
								this.spinner.hide();
								this.scrollToTop();
							}
						}
					} else {
						await this.postBasketCheckout(
							this.selectedLocation,
							this.basketService.currentBasket,
							this.member
						);
					}
					resolve();
				});
			} else {
				resolve();
			}
		}); });
	}

	backToCalendar() {
		this.router.navigate(['/calendar']);
	}

	async checkout() {
		this.alertService.clear();
		this.submitted = true;
		let noNotifications = true;

		const clientCreated = await this.createClient(this.adminJourney);
		if (clientCreated) {
			console.log('client created');
			try {
				await this.addItem();
			} catch (err) {
				// addItem() has already surfaced the error to the customer. Do not fall through:
				// the service item can still hold whatever resource JRNI auto-assigned, which is
				// exactly how bookings land on the wrong resource.
				console.error('Aborting checkout — service item update failed', err);
				this.checkoutStarted = false;
				this.spinner.hide();
				return;
			}

			// set member as the client,if the login is not required
			if (
				this.departmentService.selectedDepartment.hasOwnProperty('extra') &&
				this.departmentService.selectedDepartment.extra.hasOwnProperty(
					'login_required'
				)
			) {
				this.departmentService.selectedDepartment.extra.login_required.toLowerCase() ===
					'yes'
					? (this.member = await this.memberService.getMember())
					: (this.member = await this.datastoreService.client);
			} else {
				this.member = await this.datastoreService.client;
			}

			// if (!this.datastoreService.adminJourney && this.datastoreService.selectedService.extra?.online_payment == 'Yes') {
			noNotifications = true;
			// }

			await this.basketService
				.checkoutBasketV1(this.selectedLocation, this.member, noNotifications)
				.then(async (res: any) => {
					// Get the purchase
					const purchase: any = await this.apiService.findPurchase(
						this.selectedLocation,
						res.id
					);
					console.log(purchase);

					const booking = purchase._embedded.bookings[0];
					this.datastoreService.booking = booking;
					this.datastoreService.purchase = purchase;
					//
					console.log(booking);

					// await this.apiService.sendFrontSheetEmail(booking); // sendCommunicationEmail
					let ceremonyFee;
					try {
						if (this.datastoreService.serviceFees.length > 0) {
							// find ceremony base cost
							const noQuestionId = this.datastoreService.serviceFees.find(
								(item) => item.questionid == undefined
							);
							console.log('noQuestionId', noQuestionId);
							if (noQuestionId) {
								ceremonyFee = noQuestionId;
								// Leave the base cost in serviceFees - the fee table renders that
								// array, and the extra-item loop below skips rows with no questionid.
							}
						}

						// ceremonyFee = this.datastoreService.serviceFees.shift();
					} catch (err) {
						console.log(err);
					}
					console.log(
						'this.datastoreService.adminJourney',
						this.datastoreService.adminJourney,
						'this.datastoreService.selectedService.extra?.online_payment',
						this.datastoreService.selectedService.extra?.online_payment,
						'this.datastoreService.serviceFees',
						this.datastoreService.serviceFees,
						'ceremonyFee',
						ceremonyFee
					);
					let paymentAmount = 0;
					let bookingPrice = 0;
					let chargePrice = 0;
					let certs = 0;
					let foundExtraItemIds = [];
					let foundExtraItemCharges = [];

					let certsToRemoveCount = 0;
					if (this.adminJourney) {
						const certsToRemove =
							this.adminForm.controls['certificate_removal'];
						if (parseInt(certsToRemove.value) > 0) {
							console.log(
								`We should remove ${certsToRemove.value} certificates`
							);
							certsToRemoveCount = parseInt(certsToRemove.value);
						}
					}

					try {
						// link booking to extra item ONLY IF we are not an admin journey, but we always want to calculate the price.
						if (this.datastoreService.config.hasOwnProperty('journey_config')) {
							if (
								this.datastoreService.config.journey_config.hasOwnProperty(
									'ceremonies'
								)
							) {
								// if (this.datastoreService.config.journey_config.ceremonies.hasOwnProperty('fees_extra_item_id')) {
								//   const extraItem = this.datastoreService.config.journey_config.ceremonies.fees_extra_item_id.trim();
								//   await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItem, ceremonyFee);
								// }
								if (
									this.datastoreService.selectedService.extra
										?.fees_extra_item_id
								) {
									const extraItem =
										this.datastoreService.selectedService.extra?.fees_extra_item_id.trim();
									if (extraItem && extraItem.length > 0) {
										// paymentAmount += ceremonyFee.deposit;
										// paymentAmount = this.datastoreService.totalCost;
										bookingPrice += ceremonyFee.cost;
										// if (!this.adminJourney) {
										await this.apiService.linkExtraItem(
											this.selectedLocation.id,
											this.datastoreService.selectedService.id,
											booking.id,
											extraItem,
											ceremonyFee
										);
										// }
									}
								}
								// now link other items
								console.log('serviceFees', this.datastoreService.serviceFees);
								for (const extraItem of this.datastoreService.serviceFees) {
									if (extraItem.questionid) {
										const question =
											this.datastoreService.companyQuestions.find(
												(q) => q.id == extraItem.questionid
											);
										if (question) {
											if (question.help_text) {
												const schema: any = JSON.parse(question.help_text);
												if (schema['extra-item']) {
													const extraItemId = schema['extra-item'];
													foundExtraItemIds.push(extraItemId);
													foundExtraItemCharges.push(
														(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0)
													);
													// if (question.id == this.datastoreService.config.journey_config.ceremonies.included_certificates_question) {
													// paymentAmount += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
													// bookingPrice += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
													// chargePrice += Number(extraItem.cost) || 0;
													bookingPrice +=
														(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
													// bookingPrice += extraItem.cost;
													// }
													certs++;
													// Always link extra items
													// if (!this.adminJourney) {
													await this.apiService.linkExtraItem(
														this.selectedLocation.id,
														this.datastoreService.selectedService.id,
														booking.id,
														extraItemId,
														extraItem
													);
													// }
												} else {
													console.log('Missing extra item');
												}
											} else {
												console.log('Missing schema');
											}
										} else {
											console.log(`Cant find question ${extraItem.questionid}`);
											chargePrice += Number(extraItem.cost) || 0;
											bookingPrice +=
												(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
											await this.apiService.linkExtraItem(
												this.selectedLocation.id,
												this.datastoreService.selectedService.id,
												booking.id,
												extraItem.item_id,
												extraItem
											);
										}
									} else {
										console.log('No question ID on extra item');
										chargePrice += Number(extraItem.cost) || 0;
										bookingPrice +=
											(Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0);
										await this.apiService.linkExtraItem(
											this.selectedLocation.id,
											this.datastoreService.selectedService.id,
											booking.id,
											extraItem.item_id,
											extraItem
										);
									}
								}
							}
						}
					} catch (err) {
						console.log(err);
					}

					if (
						!this.datastoreService.adminJourney &&
						this.datastoreService.selectedService.extra?.online_payment ==
						'Yes' &&
						ceremonyFee &&
						ceremonyFee.hasOwnProperty('deposit')
					) {
						try {
							console.log(`Booking price: ${bookingPrice}`);
							// now we update the price of the booking
							await this.apiService.updatePrice(
								this.selectedLocation,
								booking.id,
								purchase.id,
								`${bookingPrice * 100}`,
								'0'
							);

							localStorage.setItem('booking', JSON.stringify(booking));
							// set the resource, the date time and the location, the booking all on localstorage
							localStorage.setItem(
								'resource',
								JSON.stringify(this.selectedResourceObj)
							);
							localStorage.setItem('date', JSON.stringify(this.selectedSlot));
							localStorage.setItem(
								'location',
								JSON.stringify(this.selectedLocation)
							);
							localStorage.setItem('booking', JSON.stringify(booking));
							localStorage.setItem(
								'service',
								JSON.stringify(this.selectedService)
							);
							const extraItemId =
								this.datastoreService.selectedService.extra?.fees_extra_item_id.trim();
							let extraItem;
							const extraItemBonus = [];
							try {
								extraItem = await this.apiService.getExtraItem(
									booking.company_id,
									this.selectedService.global_id,
									extraItemId
								);
								for (let i in foundExtraItemIds) {
									res = await this.apiService.getExtraItem(
										booking.company_id,
										this.selectedService.global_id,
										foundExtraItemIds[i]
									);
									if (res) {
										res.chargePrice = foundExtraItemCharges[i];
										extraItemBonus.push(res);
									}
								}
							} catch (err) {
								console.error(err);
							}
							console.log('extraItems');
							console.log(extraItem);
							console.log(extraItemBonus);
							// const redir = await this.capitaService.invoke(
							//   this.selectedLocation,
							//   booking,
							//   purchase,
							//   this.member,
							//   this.selectedService.name,
							//   extraItem ? extraItem.data.gl_code : '',
							//   this.datastoreService.totalCost * 100, // (paymentAmount * 100),
							//   false,
							//   extraItem ? extraItem.data.short_code : '',
							//   extraItemBonus
							// );
							// if (redir && redir['invoke_status'] === 'SUCCESS') {
							//   localStorage.setItem('scp', redir['scp']);
							//   window.location = redir['url'];
							// }
						} catch (err) {
							console.error(err);
							// this.apiService.logPaymentExtra(
							//   this.selectedLocation,
							//   this.selectedService,
							//   purchase.id,
							//   {
							//     "purchase": purchase,
							//     "invoke_error": err
							//   }
							// );
							this.basketService.deleteAllBaskets(this.selectedLocation);
							this.spinner.hide();
							this.checkoutStarted = false;
						}
					} else {
						// update booking price
						// let questionPrice = 0
						// for (const q of booking._embedded.answers) {
						//   if (q.question_id == 34666) {
						//     if (q.value > 0) {
						//       questionPrice = q.value
						//       const newPrice = (questionPrice * 100) + booking.price;
						//       await this.apiService.updatePrice(this.selectedLocation, booking.id, booking.purchase_id, newPrice, booking.paid);
						//     }
						//   }
						// }
						// if (this.datastoreService.config.hasOwnProperty('journey_config')) {
						//   if (this.datastoreService.config.journey_config.hasOwnProperty('ceremonies')) {
						//     if (this.datastoreService.config.journey_config.ceremonies.hasOwnProperty('included_certificates_question')) {
						//       const includedCerts = this.datastoreService.config.journey_config.ceremonies.included_certificates_question;
						//       if (this.datastoreService.config.journey_config.ceremonies.included_certificates_question.length > 0) {
						//         let certsToAdd = 0;
						//         for (const extraItem of this.datastoreService.serviceFees) {
						//           if (extraItem.questionid) {
						//             const question = this.datastoreService.companyQuestions.find((q) => q.id == extraItem.questionid);
						//             // Only do this for initial included certificates
						//             console.log(includedCerts);
						//             console.log('question', question);
						//             const foundItem = includedCerts.find((q:any) => q.question_id == question.id);
						//             console.log('foundItem', foundItem);
						//             if (foundItem) {
						//               if (question && question.id === foundItem.question_id) {
						//                 if (question.help_text) {
						//                   const schema: any = JSON.parse(question.help_text);
						//                   if (schema['extra-item']) {
						//                     const extraItemId = schema['extra-item'];
						//                     if (!foundExtraItemId) {
						//                       foundExtraItemId = extraItemId;
						//                     }
						//                     console.log(extraItem);
						//                     let itemQty;
						//                     if (extraItem.quantity) {
						//                       itemQty = parseInt(extraItem.quantity);
						//                       if (certsToRemoveCount > 0) {
						//                         itemQty -= certsToRemoveCount;
						//                         if (itemQty < 0) {
						//                           itemQty = 0;
						//                         }
						//                       }

						//                       if (itemQty > 0) {
						//                         console.log('options', question.options, question.options.length-1, certsToRemoveCount, question.options[(question.options.length-1) - itemQty]);
						//                         console.log(bookingPrice);
						//                         bookingPrice -= (question.options[(question.options.length-1) - itemQty].price / 100);
						//                         console.log(bookingPrice);
						//                         extraItem.quantity = certsToRemoveCount;
						//                         // certs += certsToRemoveCount;
						//                         await this.apiService.linkExtraItem(this.selectedLocation.id, this.datastoreService.selectedService.id, booking.id, extraItemId, extraItem, 'subtract');
						//                       }
						//                       if (itemQty === 0) {
						//                         bookingPrice -= (question.options[question.options.length-1].price / 100);
						//                       }
						//                     }
						//                     // paymentAmount += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));
						//                     // bookingPrice += ((Number(extraItem.cost) || 0) * (parseInt(extraItem.quantity, 10) || 0));

						//                   }
						//                   else {
						//                     console.log('Missing extra item');
						//                   }
						//                 }
						//                 else {
						//                   console.log('Missing schema');
						//                 }

						//               }
						//               else {
						//                 console.log(`Cant find question ${extraItem.questionid}`);
						//               }
						//             }

						//           }
						//           else {
						//             console.log('No question ID on extra item');
						//           }
						//         }
						//       }
						//     }
						//   }

						// }
						// now we update the price of the booking
						let extraItems = [];
						if (foundExtraItemIds) {
							for (let foundExtraItemId of foundExtraItemIds) {
								const extraItem = await this.apiService.getExtraItem(
									booking.company_id,
									this.datastoreService.selectedService.global_id,
									foundExtraItemId
								);
								extraItems.push(extraItem);
							}
						}
						let note = `Payment type: `;
						let payCallNote = '';
						let hasDeposit: boolean = false;
						const authToken: string = localStorage.getItem('auth_token');
						console.log('authToken', authToken);
						const extra: any =
							await this.apiService.getServiceItemExtraLinksForBooking(
								booking.company_id,
								booking.id,
								authToken,
								this.member.id
							);

						if (extra && extra.length > 0) {
							for (const item of extra) {
								if (
									item.item_id !==
									this.datastoreService.selectedService.extra
										?.fees_extra_item_id
								) {
									if (item.data && item.data.quantity) {
										certs = parseInt(item.data?.quantity);
									}
									break;
								}
							}
							for (const item of extra) {
								if (
									item.item_id ===
									this.datastoreService.selectedService.extra
										?.fees_extra_item_id
								) {
									// now we know the deposit and full fee value
									if (item.data.deposit !== item.data.cost) {
										// const ceremonyDate = moment(this.booking.datetime);
										const daysAway = moment(booking.datetime).diff(
											moment(),
											'd'
										);
										if (daysAway > 182) {
											note = `${note} Ceremony Fee Deposit`;
											payCallNote = `Ceremony Fee Deposit`;
											if (certs > 0) {
												note = `${note} & Certificates in Full`;
												payCallNote = `${payCallNote} & Certificates in Full`;
											}
											hasDeposit = true;
										} else {
											note = `${note} Ceremony Fee`;
											payCallNote = `Ceremony Fee`;
											if (certs > 0) {
												note = `${note} & Certificates in Full`;
												payCallNote = `${payCallNote} & Certificates in Full`;
											}
										}
									} else {
										note = `${note} Ceremony Fee`;
										payCallNote = `Ceremony Fee`;
										if (certs > 0) {
											note = `${note} & Certificates in Full`;
											payCallNote = `${payCallNote} & Certificates in Full`;
										}
									}
								}
							}
						}
						if (this.adminJourney) {
							note = `${note} ${this.form.note.value}`;
							payCallNote = `${this.form.note.value}`;
						}

						note += ` | Deposit? ${hasDeposit ? 'Yes' : 'No'}`;
						if (certs > 0 && extraItems) {
							for (let extraItem of extraItems) {
								note += `| Selected available extra: ${extraItem.item_name}`;
							}
							note += ` | Quantity: ${certs}`;
						}
						note += ` | Deposit payment date: ${moment()
							.tz('Europe/London')
							.format('dddd, MMMM Do YYYY')}`;
						if (this.adminJourney) {
							note += ` | Deposit payment amount: £${parseFloat(
								this.form.amount.value
							)}`;
							note += ` | Deposit receipt number: ${this.form.reference.value}`;

							console.log(this.form);
						}
						try {
							await this.apiService.updatePrice(
								this.selectedLocation,
								booking.id,
								booking.purchase_id,
								`${bookingPrice * 100}`,
								`0`
							);
							if (this.adminJourney) {
								await this.apiService.payPurchase(
									this.selectedLocation.id,
									booking.service_id,
									booking.purchase_id,
									{
										amount: parseFloat(this.form.amount.value),
										transaction_id: `${this.form.reference.value}`,
										notify: false,
										notify_admin: false,
										payment_status: 'paid',
										notes: `${payCallNote}`,
									},
									booking._embedded.client.id
								);
							}
							await this.apiService
								.addNoteToBooking(this.selectedLocation, booking.id, note)
								.then(async (body: any) => {
									// Loop over booking.questions and attach images for base64 answers
									for (const key in body.questions) {
										if (body.questions.hasOwnProperty(key)) {
											const question = body.questions[key];

											if (
												question.answer &&
												typeof question.answer === 'string' &&
												UPLOADED_FILE_ANSWER.test(question.answer)
											) {
												try {
													await this.basketService.attachImageToBooking(
														body.purchase_ref,
														body.id,
														question.answer
													);
													console.log(
														`Attached image for question: ${question.name}`
													);
												} catch (error) {
													console.error(
														`Error attaching image for question: ${question.name}`,
														error
													);
												}
											}
										}
									}
								});
						} catch (err) {
							console.error(err);
						}

						if (this.datastoreService.clientCreated) {
							this.datastoreService.clientCreated = false;
							try {
								// await this.apiService.sendWelcomeEmail(
								//   this.selectedLocation.id,
								//   this.member.id
								// );
								// await this.apiService.bookingTimeline(
								// 	booking.company_id,
								// 	booking.purchase_id,
								// 	this.member.id,
								// 	this.member.email
								// );
							} catch (err) {
								console.error(err);
							}
						}
						// set the resource, the date time and the location, the booking all on localstorage
						localStorage.setItem(
							'resource',
							JSON.stringify(this.selectedResourceObj)
						);
						localStorage.setItem('date', JSON.stringify(this.selectedSlot));
						localStorage.setItem(
							'location',
							JSON.stringify(this.selectedLocation)
						);
						localStorage.setItem('booking', JSON.stringify(booking));
						localStorage.setItem(
							'service',
							JSON.stringify(this.selectedService)
						);
						this.router.navigate(['/confirmation']);
					}
				})
				.catch((error: HttpErrorResponse) => {
					console.log(error);
					if (error['response'] && error['response'].status === 409) {
						this.alertService.error(
							this.translateService.instant('SUMMARY.INVALID_SLOT')
						);
					} else {
						this.alertService.error(
							this.translateService.instant('COMMON.LOAD_ISSUE')
						);
					}
					this.spinner.hide();
					this.checkoutStarted = false;
					this.scrollToTop();
				});
		}
	}

	formatQuestion(value) {
		// Check if the alue matches the date pattern
		const datePattern = /^\d{4}-\d{2}-\d{2}$/;
		if (datePattern.test(value)) {
			const [year, month, day] = value.split('-');
			return `${day}-${month}-${year}`; // Format as "DD-MM-YYYY"
		}

		// Check if the value is "true" or "false"
		if (value === 'true' || value === true) {
			return 'Yes';
		}
		if (value === 'false' || value === false) {
			return 'No';
		}

		// Return the value as-is for other cases
		return value;
	}

	// Scroll to the top of the page
	scrollToTop() {
		const scrollToOptions = { top: 0, left: 0, behavior: 'instant' };
		window.scrollTo(scrollToOptions as unknown as ScrollToOptions);
	}
}
